import SlideQuiz from "./slide-quiz";
import QuizTitles from "./quiz-titles";
import Graph from "../d3/graph";
import AssessmentManager, { removePieAssessment, createPieAssessment } from "../assessment/assessment-manager";
import { simplifyFraction } from "../assessment/assessment-math";
import Utils from "../utils";
import RewardScreens from "../rewardScreens/rewardScreens";
import { goToNextVisibleSlide, getNextVisibleSlideIndex, stopAutoplay, setNextPrevEnabled } from "../slides/add-slide-controls";
import Analytics from "../analytics";
import SlideManager from "../utils/slide-manager";
import MeshIsolation from "../animations/meshIsolation";
import Transition from "../2DObjects/Transition";
import { embibeTempQuestion, embibeTempExplaination } from "./tempQuizData";
import RenderManager from "../rendering/render-manager";
import resize from "../utils/resize";
import getVWKey from "../helpers/getVWKey";
import * as Events from "../utils/events";
import AssetLibrary from "../assets/asset-library";
import getTabIndex from "../utils/tab-index";
import AssetsBusy from "../assets/assets-busy";
import translations from "./translations.js";
import ensureArray from '../utils/ensure-array';
import { setFuzzyObjectSelection } from '../interactivity/add-interactivity';
import transferTransform, { transferTransformMatrix } from "../utils/transfer-transform";
import LayersManager from "../layers/LayersManager";
import getObjName from "../helpers/getObjName.js";

let quizId = false;
const tmpMatrix = new THREE.Matrix4();

const minFontSize = () => {
    return parseFloat(document.documentElement.style.fontSize); //px;
}

const QuizManager = new class QuizManager {
    constructor() {
        this._resets = [];

        this._timer = 0;
        this._score = 0;
        this._answered = 0;

        this._slide = 0;

        this._quizInterval = null;

        this._anchoredLabel = null;

        this._dom = {
            totalScore: null,
            quizWrap: null,
            quizCnt: null,
            scoreEl: null,
            slideQuizScoreEl: null,
            slideQuizScore: null,
            timeEl: null,
            quizBox: null,
            quizReqCnt: null,
            quizNotReqCnt: null
        };

        this._onResize = null;

        this._flipState = false;
        this._meshStates = {};
        this.setFontSize = (titleEl, scrollEl) => setFontSize(titleEl, scrollEl, Q3.SlideManager.currentSlide.slideQuiz.questionFontSize);
    }

    deleteObjectFromQuiz(object) {
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (slideQuiz) {
            let uuidsToDelete = new Set();
            object.traverse(mesh => {
                uuidsToDelete.add(mesh.uuid);
            })
            const type = slideQuiz.type;
            switch (type) {
                case "plopQuiz": {
                    uuidsToDelete.forEach(uuid => {
                        let ind = slideQuiz.meshes.findIndex(m => m.uuid === uuid);
                        if (ind !== -1) {
                            let assessment = slideQuiz.meshes.splice(ind, 1)[0];
                            Q3.AssessmentManager.destroy(assessment);
                        }
                    })
                    break;
                }
                case "isolated":
                case "select": {
                    let mesh = slideQuiz.meshes[0];
                    if (mesh) {
                        let quizUuids = [mesh.uuid[0], ...(mesh.additional || []).map(m => m.uuid[0])].filter(uuid => !uuidsToDelete.has(uuid));
                        console.log(uuidsToDelete, quizUuids);
                        if (quizUuids.length) {
                            slideQuiz.meshes[0] = {
                                uuid: [quizUuids[0]],
                                name: quizUuids.map((uuid) => getObjName(Q3.assets.objects[uuid])).join(", ")
                            };
                            if (quizUuids.length > 1) {
                                slideQuiz.meshes[0].additional = quizUuids.slice(1).map(uuid => ({ uuid: [uuid] }))
                            }
                        } else {
                            slideQuiz.meshes = [];
                        }
                    }
                    break;
                }
                default: {
                    break;
                }
            }
        }
    }

    convertEmbibeQuiz(data) {
        let quiz;

        function cleanupStr(str) {
            return str.replace(/&nbsp;/g, " ").replace(/\n*\r*/g, "");
        }

        const embibeTypesMap = {
            MatrixMatchSingleChoice: "radio",
            SingleChoice: "radio",
            MultipleChoice: "radio",
            TrueFalse: "bool",
            MultipleFillInTheBlanks: "ftb",
            FillInTheBlanks: "ftb",
            MatrixMatchGrid: "matrix",
        }

        const quizLangKeyMap = {
            "english": "en",
            "hindi": "hi"
        }
        if (data && data.subtype && data.content && data.content.question_details && data.content.question_details[quizLangKeyMap[Q3.cooboLang]]) {
            let quizData = data.content.question_details[quizLangKeyMap[Q3.cooboLang]];
            try {
                quiz = new SlideQuiz();
                if (embibeTypesMap[data.subtype]) {
                    quiz.type = embibeTypesMap[data.subtype];
                    if (quizData.question_txt) {
                        quiz.question = cleanupStr(quizData.question_txt)
                    }
                    if (quizData.hints && quizData.hints.length) {
                        quiz.hintsEnabled = true;
                        quiz.hints = quizData.hints.sort((a, b) => Number(a.display_order || "0") < Number(b.display_order || "0") ? 1 : -1).map(h => ({
                            type: "text",
                            value: cleanupStr(h.body)
                        }))
                    }
                    quiz.embibeConvertedQuestionId = data.id;
                    quiz.invertImages = true;
                    switch (quiz.type) {
                        case "radio": {
                            quiz.radioQuiz.answers = quizData.answers.map(a => ({ text: cleanupStr(a.body), correct: a.is_correct }))
                            break;
                        }
                        case "bool": {
                            quiz.rightAnswer = (quizData.answers.find(a => a.is_correct).body || "true").toUpperCase();
                            break;
                        }
                        case "matrix": {
                            // quiz.rightAnswer = (quizData.answers.find(a => a.is_correct).body || "true").toUpperCase();
                            if (quizData.answers[0] && quizData.answers[0].body) {
                                let split = quizData.answers[0].body.split(";;");
                                let [rows, cols] = JSON.parse(split[0]).map(arr => arr.sort());
                                quiz.matrixRows = rows.length;
                                quiz.matrixCols = cols.length;
                                quiz.rightAnswer = split[1].split(";").filter(s => s).map(a => `${rows.indexOf(a[0])} ${cols.indexOf(a[2])}`)
                            }
                            break;
                        }
                        case "ftb": {
                            quiz.question = "";
                            let ftbType = quizData.subtype === "FillInTheBlanks" ? "input" : "capsule"
                            let split = {
                                input: /\.+/g,
                                capsule: /_+/g
                            }
                            quiz.fillTheBlanksQuiz.wrongAnswers = "";
                            quiz.fillTheBlanksQuiz.ftbType = ftbType;
                            quiz.fillTheBlanksQuiz.question = quizData.question_txt.split(split[ftbType]);
                            quiz.fillTheBlanksQuiz.answers = quizData.answers.map(a => quiz.fillTheBlanksQuiz.answerByType({ value: cleanupStr(a.body) }, ftbType))
                            break;
                        }
                        default:
                            break;
                    }
                } else {
                    quiz.embibeQuestionId = data.id;
                }

            } catch (err) {
                console.log(err.message);
                quiz = false
            }
        }
        return quiz;
    }

    _resetFlipState() {
        Object.keys(this._meshStates).forEach(key => {
            /**
             * @type {import("../assessment/assessment-mesh-plop").AssessmentMeshPlop}
             */
            const ass = this._meshStates[key].assessment;
            const parents = ass.getParentChain(ass.object).slice(0, 2);

            ass.getCurrentInitialMatrix().copy(parents[1].matrix);

            parents[1].matrix
                .decompose(ass.plopObject.position, ass.plopObject.quaternion, ass.plopObject.scale);

            tmpMatrix
                .fromArray(this._meshStates[key].matrix)
                .decompose(parents[1].position, parents[1].quaternion, parents[1].scale);

            delete this._meshStates[key];
        });
    }

    _enableFlipState() {
        AssessmentManager.assessments.forEach(ass => {
            const parents = ass.getParentChain(ass.object);
            const original = new THREE.Matrix4().copy(parents.slice(-1)[0].matrixWorld);

            this._meshStates[ass.object.uuid] = {
                assessment: ass,
                matrix: parents[1].matrix.toArray()
            };

            tmpMatrix.identity();
            parents.slice(2).forEach(object => tmpMatrix.multiply(object.matrix));
            parents[1].matrix.getInverse(tmpMatrix);

            transferTransform(ass.plopObject, parents[1])
                .premultiply(tmpMatrix)
                .decompose(parents[1].position, parents[1].quaternion, parents[1].scale);

            transferTransformMatrix(original, ass.plopObject);
        });
    }

    set isSwitchedDirection(v) { SlideManager.currentSlide.slideQuiz.isSwitchedDirection = v; }
    get isSwitchedDirection() { return SlideManager.currentSlide.slideQuiz.isSwitchedDirection; }

    set isCommutable(v) { SlideManager.currentSlide.slideQuiz.isCommutable = v; }
    get isCommutable() { return SlideManager.currentSlide.slideQuiz.isCommutable; }

    set displayDropzone(v) { SlideManager.currentSlide.slideQuiz.displayDropzone = v; }
    get displayDropzone() { return SlideManager.currentSlide.slideQuiz.displayDropzone; }

    set useSnapping(v) { SlideManager.currentSlide.slideQuiz.useSnapping = v; }
    get useSnapping() { return SlideManager.currentSlide.slideQuiz.useSnapping; }

    resetPositions() {
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (!(slideQuiz && slideQuiz.active && slideQuiz.type === "plopQuiz")) return;

        AssessmentManager.assessments.forEach(ass => {
            ass.restoreToOriginal();
        });
    }

    switchAssessmentDirections(ass) {
        const animData = SlideManager.currentSlide.animation[ass.object.uuid];

        if (!this.isSwitchedDirection) {
            ass.restoreToOriginal();
            ass.object.updateMatrix();
            ass.plopObject.updateMatrix();
        }

        tmpMatrix.copy(ass.object.matrix);
        ass.object.matrix
            .copy(ass.plopObject.matrix)
            .decompose(ass.object.position, ass.object.quaternion, ass.object.scale);
        ass.plopObject.matrix
            .copy(tmpMatrix)
            .decompose(ass.plopObject.position, ass.plopObject.quaternion, ass.plopObject.scale);

        ass.getCurrentInitialMatrix().copy(ass.plopObject.matrix);
        Object.assign(animData.position, {
            x: ass.object.position.x,
            y: ass.object.position.y,
            z: ass.object.position.z
        });

        Object.assign(animData.rotation, {
            x: ass.object.rotation.x,
            y: ass.object.rotation.y,
            z: ass.object.rotation.z
        });

        Object.assign(animData.scale, {
            x: ass.object.scale.x,
            y: ass.object.scale.y,
            z: ass.object.scale.z
        });
    }

    switchDirections() {
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (!(slideQuiz && slideQuiz.active && slideQuiz.type === "plopQuiz")) return;

        this.isSwitchedDirection = !this.isSwitchedDirection;

        AssessmentManager.assessments.forEach(this.switchAssessmentDirections.bind(this));
    }

    flipMeshStates() {
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (!(slideQuiz && slideQuiz.active && slideQuiz.type === "plopQuiz")) return;

        this._flipState = !this._flipState;

        if (!this._flipState) this._resetFlipState();
        else this._enableFlipState();
    }

    disableMeshStates() {
        if (!this._flipState) return;
        this._flipState = false;
        this._resetFlipState();
    }

    get quizCompactLayout() {
        let slideQuiz = SlideManager.currentSlide.slideQuiz;
        return slideQuiz && slideQuiz.active && slideQuiz.layoutMobile === "compact"
    }

    get isStateFlipped() { return this._flipState; }

    get timer() { return this._timer; }
    set timer(v) { this._timer = v; }

    get translation() { return translations[Q3.cooboLang]; }
    get QuizTitles() { return QuizTitles[Q3.cooboLang]; }

    //totalUserScore
    get score() { return this._score; }
    set score(v) {
        this._score = v;
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (v === 0 || slideQuiz.type === 'selectObject') this._divScore.innerHTML = "";
        else this._divScore.innerHTML = `Total score: <span>${v}</span>`;
    }

    get answers() { return this._answered; }
    set answers(v) { this._answered = v; }

    setScoreContainer(div) { this._divScore = div; }

    setSlideQuizElements({
        quizWrap, quizCnt, scoreEl, slideQuizScoreEl, slideQuizScore, timeEl, hintsCnt, hintsContent
    }) {
        this._dom.quizWrap = quizWrap;
        this._dom.quizCnt = quizCnt;
        this._dom.scoreEl = scoreEl;
        this._dom.slideQuizScoreEl = slideQuizScoreEl;
        this._dom.slideQuizScore = slideQuizScore;
        this._dom.timeEl = timeEl;
        this._dom.hintsCnt = hintsCnt;
        this._dom.hintsContent = hintsContent;
    }

    createNewSlideQuiz(type) { return new SlideQuiz({ type }); }
    reCreateSlideQuiz(data) { return new SlideQuiz(data); }

    initQuizes(data) {
        function isVisibleInSlide(slide, objectUuid) {
            let parent = data.assets.objects[objectUuid];

            if (!parent) return false;

            let parentData = slide.animation[objectUuid];
            let visible = parentData.visible !== false && parentData.deleted !== true;

            while (parent && !parent.isHiddenRoot && visible) {
                const parentUuid = parent.parent;

                if (!parentUuid || !data.assets.objects[parentUuid]) break;

                parent = data.assets.objects[parentUuid];

                if (parent.isHiddenRoot) break;

                parentData = slide.animation[parentUuid];
                visible = visible && parentData.visible !== false && parentData.deleted !== true;
            }

            return visible;
        }

        AssessmentManager.clearAssessments();

        if (!Q3.editMode) {
            data.slides.forEach(slide => {
                const quizData = slide.slideQuiz;
                const isValid = quizData && quizData.active ? Object.keys(quizData).length > 0 : false;

                if (!isValid) return;

                if (quizData.type === "select") {
                    quizData.meshes = quizData.meshes
                        .map(mesh => {
                            const rootVisible = isVisibleInSlide(slide, mesh.uuid[0]);
                            const subs = mesh.additional instanceof Array ? mesh.additional.map(mesh => [mesh, isVisibleInSlide(slide, mesh.uuid[0])]) : [];

                            if (!mesh.additional)
                                mesh.additional = subs.filter(([_, isVisible]) => isVisible).map(([mesh, _]) => mesh);

                            if (!rootVisible)
                                mesh.uuid = mesh.additional && mesh.additional.length > 0 ? mesh.additional.shift().uuid : null;

                            return mesh;
                        })
                        .filter(mesh => !!mesh.uuid);

                    if (quizData.meshes.length === 0)
                        slide.slideQuiz = null;
                }
            });
        }

        this._resets = data.slides.map(slide => {
            const quizData = slide.slideQuiz;
            const isValid = quizData && quizData.active ? Object.keys(quizData).length > 0 : false;

            if (!isValid) {
                return null;
            } else {

                slide.slideQuiz = new SlideQuiz(quizData);

                if (slide.slideQuizReset) delete slide.slideQuizReset;

                return slide.slideQuiz;
            }
        });
    }

    resetQuizes() {
        Q3.data.slides.forEach((slide, i) => {
            slide.slideQuiz = new SlideQuiz(this._resets[i]);
        });

        this.score = 0;

        Object.keys(Q3.data.quizData).forEach(uuid => {
            delete Q3.data.quizData[uuid].userAnswer;
            delete Q3.data.quizData[uuid].userScore;
        });

        $(".livelabel .quiz-button").removeClass("complete fail");
        $(".livelabel .quiz-button .quiz-score").empty();
        $("#qbix-viewer-button-previous").addClass("disabled");

        setTimeout(() => {
            this.initSlideQuiz(0);
            this._answered = 0;
        }, 500);
    }

    /**
     * @return {number}
     */
    get quizCount() {
        return Q3.data.slides.reduce((count, s) => {
            if (s.slideQuiz && s.slideQuiz.active) count++;
            return count;
        }, 0);
    }

    beforeTransition() {
        this.clear(true);
        if (SlideManager.currentSlide.slideQuiz && SlideManager.currentSlide.slideQuiz.type === "draglabels") {
            $('.overlay2d').addClass('hide-labels');
        }
    }

    clear(instant) {
        return new Promise(resolve => {
            let quizWrap = this._dom.quizWrap;
            let hintsCnt = this._dom.hintsCnt;
            let hintsContent = this._dom.hintsContent;
            let quizCnt = this._dom.quizCnt;
            let slideQuizScoreEl = this._dom.slideQuizScoreEl;
            let slideQuizScore = this._dom.slideQuizScore;
            hintsCnt.removeClass("active")
            quizWrap.fadeOut(instant ? 0 : 150, () => {
                quizWrap.removeClass(quizWrap[0].className.split(" ").filter(c => c.startsWith("quiztype-")).join(" "));
                quizWrap.removeClass('mobile-compact');
                quizWrap.removeClass('half-screen');
                quizWrap.removeClass('two-columns');
                quizWrap.removeClass('is-anchored');
                quizWrap.removeClass('embibe-quiz');
                quizCnt.removeClass('complete fail correct wrong');
                quizWrap.removeClass('find-the-parts');
                quizWrap.removeClass('connect-quiz');
                slideQuizScoreEl.removeClass("complete fail");
                slideQuizScore.html('');
                quizCnt.html("");
                hintsContent.html("");

                quizWrap.removeClass('submit-action-quiz');
                $('.answer-placeholder').removeClass('answer-placeholder correct wrong');
                $('.drop-here').remove();
                $('.overlay2d').removeClass('hide-labels');
                if (!Q3.editMode) {
                    $(".submit-cnt").css({ opacity: 1, pointerEvents: "auto" });
                }

                MeshIsolation.end();
                uninitDragDrop2D();

                !Q3.editMode && Graph.destroy();

                AssessmentManager.inAssessmentMode = false;

                $(".connect-quiz.viewer-mode").remove();
                setTimeout(() => {
                    resolve();
                }, instant ? 0 : 100)
            })
        })
    }

    isQuiz3D(slide) {
        let slideQuiz = Q3.data.slides[slide].slideQuiz;
        let quizTypes3D = ["select", "isolated", "draglabels", "plopQuiz", "interactiveCar", "fraction"];
        return slideQuiz && quizTypes3D.indexOf(slideQuiz.type) !== -1
    }

    quizHasInput(slide) {
        let slideQuiz = Q3.data.slides[slide].slideQuiz;
        let filters = [
            (q) => q.type === "textarea",
            (q) => q.type === "ftb" && q.fillTheBlanksQuiz.ftbType === "input",
        ];
        return slideQuiz && filters.filter(f => f(slideQuiz)).length
    }

    isQuizTransparent(slide = SlideManager.activeSlide) {
        let slideQuiz = Q3.data.slides[slide].slideQuiz;
        let quizTypes = ["select", "isolated", "draglabels", "plopQuiz", "interactiveCar", "fraction", "region", "dragDrop2d"];
        return quizTypes.indexOf(slideQuiz.type) !== -1
    }

    initSlideQuiz(slide) {
        if (Q3.nouicontrols || SlideManager.activeSlide !== slide) return;

        let slideQuiz = Q3.data.slides[slide].slideQuiz;
        if (!slideQuiz || !slideQuiz.active || slideQuiz.type === "embibeQuiz") {
            $("#app").removeClass("quiz-active");
            return;
        }
        if (this.isQuiz3D(slide)) {
            Q3.data.slides[slide].dynamicPerspective = false;
        }

        this._dom.quizWrap.css('pointer-events', this.isQuizTransparent(slide) ? "none" : "none");

        Q3.initializingQuiz = true;

        awaitLib()
            .catch(() => { Q3.initializingQuiz = false; })
            .then(() => {
                this._slide = slide;
                let quizWrap = this._dom.quizWrap;
                let quizCnt = this._dom.quizCnt;
                let slideQuizScoreEl = this._dom.slideQuizScoreEl;
                let slideQuizScore = this._dom.slideQuizScore;
                let timeEl = this._dom.timeEl;
                let scoreEl = this._dom.scoreEl;

                this.clear(true)
                    .catch(() => { Q3.initializingQuiz = false; })
                    .then(() => {
                        if (slideQuiz.embibeQuestionId) {
                            initEmbibeQuizIframe(quizWrap, slideQuiz)
                            Q3.initializingQuiz = false;
                            return;
                        }
                        if (slideQuiz.type === "draglabels") {
                            const meshlabels = Object
                                .keys(slideQuiz.labels)
                                .map(labelUuid => Q3.labels.live.getLabelByUuid(labelUuid)).filter(l => l);
                            if (meshlabels.length !== Object.keys(slideQuiz.labels).length) {
                                console.error("invalid quiz: missing labels")
                                return
                            }
                        }

                        quizCnt.html("");

                        if ((slideQuiz.type === "dragDrop2d" || slideQuiz.type === "plopQuiz")
                            && slideQuiz.actionsOnSubmit && slideQuiz.actions.length) {
                            quizWrap.addClass("submit-action-quiz");
                            slideQuiz.isInteractive = true;
                        } else {
                            slideQuiz.required = true;
                            setNextPrevEnabled({ next: Boolean(slideQuiz.isSubmitted), prev: true });
                            !slideQuiz.isSubmitted && stopAutoplay({ byQuiz: true });
                        }

                        quizWrap[this.isQuizTransparent(slide) ? "addClass" : "removeClass"]("is-transparent");
                        let halfscreen = slideQuiz.layout === "halfscreen";
                        let twoColumns = slideQuiz.layout === "twoColumns";
                        let compactMobile = slideQuiz.layoutMobile === "compact" && !this.quizHasInput(slide);
                        if (halfscreen) quizWrap.addClass('half-screen');
                        if (twoColumns) quizWrap.addClass('two-columns');
                        if (compactMobile) quizWrap.addClass('mobile-compact');
                        if (slideQuiz.removeMathSpacing) quizWrap.addClass('remove-math-spacing');
                        quizWrap.fadeIn(300 * Number(!Boolean(slideQuiz.isSubmitted)));
                        $("#app").addClass("quiz-active");
                        let scoreDom = [`<span class="earn">${this.translation.earn}</span>`, slideQuiz.score]
                        scoreEl.html(Q3.cooboLang === "hindi" ? scoreDom.reverse().join(" ") : scoreDom.join(" "));
                        let headingType = slideQuiz.type;
                        if (headingType === "radio") {
                            headingType += slideQuiz.radioQuiz.answers.filter(a => a.correct).length > 1 ? "_multiple" : "_single";
                        }
                        let typeHeading = $(`<h2 class='type-heading ${slideQuiz.isHideUI ? 'hide-ui' : ''}'>` + this.QuizTitles[headingType] + "</h2>");
                        let scroll = $(`<div class="quiz-scroll pointer-auto"></div>`);
                        let layoutTop = $(`<div class="quiz-layout top"></div>`);
                        let layoutMiddle = $(`<div class="quiz-layout middle"></div>`);
                        let layoutBottom = $(`<div class="quiz-layout bottom"></div>`);
                        scroll.append(layoutTop, layoutMiddle, layoutBottom);
                        let quizQuestionTitleEl = $(`<h3 class='question ${ (slideQuiz.isTextJustified ? " text-justified" : "")} ${slideQuiz.invertImages ? "invert-images" : ""}'>${slideQuiz.question}</h3>`);
                        if (slideQuiz.questionFontSize !== 1) {
                            quizQuestionTitleEl.css("font-size", `${slideQuiz.questionFontSize*2}rem`);
                        }
                        if (slideQuiz.invertImages) {
                            quizQuestionTitleEl.find("img").wrap(`<div class='img-cnt'></div>`)
                        }
                        // This was some hack but no idea why
                        // if (quizQuestionTitleEl.children().length) {
                        //     quizQuestionTitleEl.css("white-space", "normal");
                        // }
                        layoutTop.append(quizQuestionTitleEl);
                        quizWrap[0].style.removeProperty('display');

                        let submitCnt = $('<div class="submit-cnt"></div>');
                        let submitBtn = $(`<button class="quiz-msg-btn submit-btn" data-confirm="${this.translation.confirm}" data-confirm-mobile="${this.translation.confirmMobile}">${this.translation.submit}</button>`);

                        submitBtn.click((e) => { this.submitQuiz({ submitBtn }) });

                        const onSlideReady = (slideIndex) => {
                            if (getNextVisibleSlideIndex(slide) === slideIndex) {
                                skipBtn.removeClass("loading");
                                Events.unlisten("slide_ready", onSlideReady);
                            }
                        };

                        let skipBtn = $(`<button class="quiz-msg-btn skip-btn transparent loading">${this.translation.skip}</button>`);

                        if (SlideManager.slideCount > slide + 1 && !AssetsBusy.isSlideReady(slide + 1)) {
                            Events.listen("slide_ready", onSlideReady);
                        } else skipBtn.removeClass('loading');

                        if (!slideQuiz.required) submitCnt.append(skipBtn);

                        skipBtn.on("click", () => {
                            goToNextVisibleSlide({ userAction: true, ignore: true });
                        });

                        if (!slideQuiz.isSubmitted) {
                            let hints = slideQuiz.hints.filter(h => h.type === "text" ? Boolean(h.value) : Boolean(h.uuid.length));
                            let hintsCount = hints.length;

                            if (slideQuiz.hintsEnabled && hints.length) {
                                hints.forEach((h) => {
                                    if (h.type === "object")
                                        SlideManager.currentSlide.layers[h.uuid] = false;
                                });

                                LayersManager.update();

                                let hintsBtn = $(`<button class="quiz-msg-btn hints-btn transparent">${this.translation.hint}</button>`)

                                submitCnt.append(hintsBtn);

                                let hintInd = 0;

                                const showHint = () => {
                                    this._dom.hintsCnt.addClass("active");

                                    let hint = hints[hintInd];

                                    if (!hint) return;

                                    if (hint.type === "text") {
                                        let hintContent = $(`<div class="quiz-hint"><span class="hint-word">${this.translation.hint} ${hintsCount > 1 ? (hintInd + 1) : ""} : </span>${hint.value}</div>`);

                                        this._dom.hintsContent.prepend(hintContent.clone())
                                    }

                                    MathJax.typesetPromise([this._dom.hintsCnt[0]]);

                                    hintInd++;

                                    let controlHintsBtn = this._dom.hintsCnt.find(".hints-btn")

                                    controlHintsBtn.html(`${hintsCount - hintInd} Hint${hintsCount - hintInd > 1 ? "s" : ""} remaining`)

                                    if (hintInd >= hintsCount) controlHintsBtn.html('No more hints remaining');
                                    else controlHintsBtn.off("click").on("click", showHint)

                                }
                                hintsBtn.on("click", showHint)
                            }
                        }

                        if (slideQuiz.type !== "select" && slideQuiz.type !== "ferris" && slideQuiz.type !== "region")
                            submitCnt.append(submitBtn);

                        let feedbackCnt = $('<div class="feedback-cnt"></div>');

                        const params = {
                            scroll,
                            quizWrap,
                            quizCnt: layoutBottom,
                            slideQuizScoreEl,
                            slideQuizScore,
                            timeEl,
                            scoreEl,
                            quizQuestionTitleEl,
                            slide,
                            slideQuiz,
                            submitCnt,
                            submitBtn,
                            scope: this
                        };

                        Q3.container.appendChild(quizWrap[0]);
                        Q3.container.appendChild(this._dom.hintsCnt[0])

                        quizWrap.addClass(`quiztype-${slideQuiz.type}`);

                        if (slideQuiz.type === "ftb")
                            quizWrap.addClass(`quiztype-ftbType-${slideQuiz.fillTheBlanksQuiz.ftbType}`);

                        quizCnt.append(typeHeading, scroll)

                        if (slideQuiz.isHideUI) {
                            $('.score').addClass('hide-ui')
                            $('#slide-quiz-wrap').addClass('is-transparent-important')
                        } else {
                            $('.score').removeClass('hide-ui')
                            $('#slide-quiz-wrap').removeClass('is-transparent-important')
                        }
                        // if (!slideQuiz.isSubmitted) {
                        switch (slideQuiz.type) {
                            case "matrix": initMatrix(params); break;
                            case "embibeTest": initEmbibeQuiz(params); break;
                            case "fraction": initFraction(params); break;
                            case "plopQuiz": initDragDrop3D(params); break;
                            case "radio": initRadio(params); break;
                            case "connect": initConnect(params); break;
                            case "drag": initDrag(params); break;
                            case "isolated": initIsolated(params); break;
                            case "draglabels": initDragLabels(params); break;
                            case "region":
                                initRegion({
                                    quizWrap,
                                    quizCnt: layoutTop,
                                    quizQuestionTitleEl,
                                    slideQuizScoreEl,
                                    slideQuizScore,
                                    slide,
                                    slideQuiz,
                                    submitCnt
                                });
                                break;
                            case "select": initSelect(params); break;
                            case "textarea": initTextArea(params); break;
                            case "bool": initBool(params); break;
                            case "ferris": quizWrap[0].style.display = 'none'; break;
                            case "interactiveCar":
                                quizWrap[0].style.display = 'none';
                                initInteractiveCar(params);
                                break;
                            case "dragDrop2d": initDragDrop2D(params); break;
                            case "ftb": initFTB(params); break;
                        }
                        quizCnt.append(feedbackCnt, submitCnt);
                        // } else {
                        //     quizCnt.html(slideQuiz.submittedDom);
                        //     $(".livelabel").addClass('hidden-by-quiz');
                        //     if (slideQuiz.type === "draglabels") {
                        //         Object.keys(slideQuiz.labels).forEach(key => {
                        //             let label = Q3.labels.live.getLabelByUuid(key);
                        //             if (slideQuiz.labels[key].enabled) {
                        //                 $(label._element.domElement).removeClass('hidden-by-quiz').addClass("answer-placeholder");
                        //                 $(label._element.domElement).append($(`<div class="drop-here">${slideQuiz.labels[key].userAnswer ? `<div class="answer">${slideQuiz.labels[key].userAnswer}</div>` : `Drop answer here!`}</div>`))
                        //                 if (slideQuiz.labels[key].userAnswer === slideQuiz.labels[key].name) {
                        //                     label._element.domElement.classList.add("correct");
                        //                 } else label._element.domElement.classList.add("wrong");
                        //
                        //             }
                        //         });
                        //         $(".answer-placeholder .reset-btn").remove();
                        //     }
                        //
                        //     if (slideQuiz.type === "plopQuiz") {
                        //         slideQuiz.assessments.forEach((a) => {
                        //             if (a.isCorrectlyPlopped) a.plop(a);
                        //         });
                        //
                        //         Q3.RenderManager.activeCamera.update = true;
                        //     } else if (slideQuiz.type === "region" && slideQuiz.regions[0] && slideQuiz.regions[0].givenAnswers) {
                        //         let objects = Object.values(LayersManager.list).filter(l => l.objects && l.objects[0]).reduce((acc, l) => {
                        //             acc[l.objects[0].uuid] = l.objects[0];
                        //             return acc
                        //         }, {});
                        //
                        //         console.log(objects);
                        //
                        //         slideQuiz.regions[0].givenAnswers.forEach(uuid => {
                        //             if (objects[uuid]) {
                        //                 objects[uuid].clickElement.classList.add([slideQuiz.regions[0].uuid, ...slideQuiz.regions[0].additional].indexOf(uuid) === -1 ? "selected-2d-wrong" : "selected-2d-correct")
                        //             }
                        //         });
                        //     }
                        // }

                        if (twoColumns) {
                            let flex = $(`<div class="d-flex justify-between flex-wrap flex-grow overflow-hidden-desktop overflow-auto-mobile relative"></div>`);
                            let c1 = $(`<div class="quiz-column w-50"></div>`);
                            let c2 = $(`<div class="quiz-column w-50"></div>`);

                            flex.append(c1, c2);
                            c1.append(layoutTop);
                            c2.append(layoutMiddle, layoutBottom);
                            quizCnt.append(flex, submitCnt);
                        }

                        this.updateQuizWrap();

                        Q3.initializingQuiz = false;
                        if (slideQuiz.isSubmitted) {
                            this.initSolution()
                        }
                    });
            });
    }

    hideQuizBox() {
        this._dom.quizBox.className = 'quiz-box';
        this._dom.quizReqCnt.classList.remove('active');
        this._dom.quizNotReqCnt.classList.remove('active');
    }

    checkQuizesAnswers(options = {}) {
        let notAnswered = 0;
        let notAnsweredReq = 0;
        let slideToCheck = options.slideToCheck ? options.slideToCheck : SlideManager.activeSlide;

        if (!Q3.data.slides[slideToCheck]) return true


        let slideQuiz = Q3.data.slides[slideToCheck].slideQuiz;

        if (!slideQuiz || slideQuiz.isInteractive) return true;
        if (slideQuiz && slideQuiz.active && typeof slideQuiz.userAnswer === 'undefined') {
            if (slideQuiz.required) notAnsweredReq++;
            else notAnswered++;
        }

        if (notAnsweredReq) return false;
        else if (notAnswered) return false;

        return true;
    }

    deactivateUI() { document.body.classList.add("quiz-disabled"); }
    activateUI() { document.body.classList.remove("quiz-disabled"); }

    initSolution(slideQuiz = Q3.SlideManager.currentSlide.slideQuiz) {
        const quizes = {
            revealAnswer: {
                dragDrop2d: () => {
                    const { dragDrop2dQuiz } = slideQuiz;
                    dragDrop2dQuiz.dragDrop.dispose();
                    const objects = {};
                    for (let layer of LayersManager.stack) {
                        let object = layer.objects[0];
                        if (object) {
                            objects[object.uuid] = object;
                        }
                    }

                    for (let regionUuid of dragDrop2dQuiz.regions) {
                        const region = objects[regionUuid];
                        if (region) {
                            $(region.highLightElement).removeClass("selected-2d-wrong");
                        }
                    }

                    dragDrop2dQuiz.images = dragDrop2dQuiz.images.filter(i => objects[i.image]);
                    let zIndex = 5 + LayersManager.stack.length;
                    const occupiedRegions = {};
                    for (let { image: uuid, regions } of dragDrop2dQuiz.images) {
                        const image = objects[uuid];
                        image.data.sizeLg = JSON.parse(JSON.stringify(image.data.sizeLg));
                        if (image) {
                            image.parentLayer.domElement.style.zIndex = zIndex++;
                            let regionUuid = regions.find(rUuid => !occupiedRegions[rUuid]);
                            let region = regionUuid ? objects[regionUuid] : objects[regions[0]]
                            if (region) {
                                occupiedRegions[region.uuid] = true;
                                $(region.highLightElement).addClass("selected-2d-correct");
                                const regionCenter = {
                                    x: region.x + region.width / 2,
                                    y: region.y + region.height / 2
                                }
                                image.sizeLg.x = regionCenter.x - image.width / 2;
                                image.sizeLg.y = regionCenter.y - image.height / 2;

                            } else {
                                image.sizeLg.x = image.data.initialPosition.x;
                                image.sizeLg.y = image.data.initialPosition.y;
                            }
                            image.redraw();
                        }
                    }
                },
                connect: () => {
                    Graph.links = slideQuiz.resultConnections.map(c =>
                        Graph.createLinks(Graph.getNode(c.source), Graph.getNode(c.target)));
                    Graph.getScore();
                },
                ftb: () => {
                    const ftbQuiz = slideQuiz.fillTheBlanksQuiz;
                    if (ftbQuiz.ftbType === "capsule") {
                        ftbQuiz.blanks.forEach((b, i) => {
                            b.submittedClassName = b.$el[0].className;
                            b.$el.removeClass("wrong").addClass("correct").html(ftbQuiz.answers[i].value)
                        })
                    } else {
                        ftbQuiz.blanks.forEach((b, i) => {
                            b.submittedClassName = b.$el[0].className;
                            b.$el.removeClass("wrong").addClass("correct").val(b.answerObj.value);
                        })
                    }
                },
                matrix: () => {
                    $(`.matrix-quiz table tr td`).removeClass("selected").css("background", "");
                    slideQuiz.rightAnswer.forEach(a => {
                        let col = +a.split(" ")[0];
                        let row = +a.split(" ")[1];
                        let el = $(`.matrix-quiz table tr:nth-child(${row + 2}) td:nth-child(${col + 2})`);
                        el.css("background", "green");
                    })
                },
                radio: () => {
                    let radioQuiz = slideQuiz.radioQuiz;
                    for (let [answerIndex, answer] of radioQuiz.answers.entries()) {
                        if (answer.correct) {
                            answer.$el.addClass("correct");
                        } else {
                            answer.$el.removeClass("selected correct wrong")
                        }
                    }
                },
                drag: () => {
                    JSON.parse(slideQuiz.rightAnswer).forEach(a => {
                        let el = $(`#slide-quiz .q-label.draggable-li[data-answer="${a}"]`);
                        el.removeClass("wrong").addClass("correct").parent().append(el)
                    })
                },
                bool: () => {
                    $("#slide-quiz .q-label").removeClass("correct wrong");
                    $(`#slide-quiz .q-label[data-answer='${slideQuiz.rightAnswer.toString().toLowerCase()}']`).addClass("correct");
                }
            },
            hideAnswer: {
                dragDrop2d: () => {
                    const { dragDrop2dQuiz } = slideQuiz;
                    dragDrop2dQuiz.dragDrop.dispose();
                    const objects = {};
                    for (let layer of LayersManager.stack) {
                        let object = layer.objects[0];
                        if (object) {
                            objects[object.uuid] = object;
                        }
                    }

                    for (let regionUuid of dragDrop2dQuiz.regions) {
                        const region = objects[regionUuid];
                        if (region) {
                            $(region.highLightElement).removeClass("selected-2d-wrong selected-2d-correct").addClass(region.data.className);
                        }
                    }
                    dragDrop2dQuiz.images = dragDrop2dQuiz.images.filter(i => objects[i.image]);
                    let zIndex = 5 + LayersManager.stack.length;
                    for (let { image: uuid } of dragDrop2dQuiz.images) {
                        const image = objects[uuid];
                        if (image) {
                            image.parentLayer.domElement.style.zIndex = zIndex++;
                            image.sizeLg.x = image.data.sizeLg.x;
                            image.sizeLg.y = image.data.sizeLg.y;
                            image.redraw();
                        }
                    }

                },
                connect: () => {
                    Graph.links = slideQuiz.userAnswer.map(c =>
                        Graph.createLinks(Graph.getNode(c.source), Graph.getNode(c.target)));
                    Graph.getScore();
                },
                ftb: () => {
                    const ftbQuiz = slideQuiz.fillTheBlanksQuiz;
                    if (ftbQuiz.ftbType === "capsule") {
                        ftbQuiz.blanks.forEach((b, i) => {
                            if (b.submittedClassName) {
                                b.$el[0].className = b.submittedClassName;
                            }
                            b.$el.html(b.userAnswer ? b.userAnswer.text : "");
                        })
                    } else {
                        ftbQuiz.blanks.forEach((b, i) => {
                            if (b.submittedClassName) {
                                b.$el[0].className = b.submittedClassName;
                            }
                            b.$el.val(b.userAnswer || "");
                        })
                    }
                },
                matrix: () => {
                    $(`.matrix-quiz table tr td`).css("background", "");
                    Object.keys(slideQuiz.userAnswer).forEach(a => {
                        let col = +a.split(" ")[0];
                        let row = +a.split(" ")[1];
                        let el = $(`.matrix-quiz table tr:nth-child(${row + 2}) td:nth-child(${col + 2})`);
                        el.addClass("selected")
                        el.css("background", slideQuiz.rightAnswer.indexOf(a) === -1 ? "red" : "green");
                    })
                },
                radio: () => {
                    let radioQuiz = slideQuiz.radioQuiz;
                    let userAnswer = new Set(slideQuiz.userAnswer);
                    for (let [answerIndex, answer] of radioQuiz.answers.entries()) {
                        if (userAnswer.has(answerIndex)) {
                            answer.$el.addClass(`selected ${answer.correct ? "correct" : "wrong"}`);
                        } else {
                            answer.$el.removeClass("selected correct wrong")
                        }
                    }
                },
                drag: () => {
                    const rightAnswer = JSON.parse(slideQuiz.rightAnswer);
                    JSON.parse(slideQuiz.userAnswer).forEach((a, i) => {
                        let el = $(`#slide-quiz .q-label.draggable-li[data-answer="${a}"]`);
                        if (i === rightAnswer.indexOf(a)) {
                            el.addClass("correct");
                        } else {
                            el.removeClass("correct").addClass("wrong");
                        }
                        el.parent().append(el)
                    })
                },
                bool: () => {
                    $("#slide-quiz .q-label").removeClass("correct wrong selected");
                    $(`#slide-quiz .q-label[data-answer='${slideQuiz.userAnswer.toString()}']`)
                        .addClass(slideQuiz.userAnswer.toString() === slideQuiz.rightAnswer.toString() ? "correct" : "wrong")
                }
            }
        }
        if (!quizes.revealAnswer[slideQuiz.type]) return;
        const submitCnt = this._dom.quizCnt.find(".submit-cnt");
        submitCnt.children().hide();
        let btn = $("<div class='quiz-msg-btn submit-btn continue'>Reveal Answer</div>");
        if (slideQuiz.isSubmitted === "wrong") {
            submitCnt.append(btn);
        }
        let showingAnswer = false;
        const showAnswer = () => {
            showingAnswer = true;
            if (quizes.revealAnswer[slideQuiz.type]) {
                return quizes.revealAnswer[slideQuiz.type]();
            } else {
                return null;
            }
        }

        const hideAnswer = () => {
            showingAnswer = false;
            if (quizes.hideAnswer[slideQuiz.type]) {
                return quizes.hideAnswer[slideQuiz.type]();
            } else {
                return null;
            }
        }

        btn.on("click", () => {
            if (showingAnswer) {
                hideAnswer();
            } else {
                showAnswer();
            }
            btn.html(`${showingAnswer ? "Hide" : "Reveal"} Answer`)
        })
        if (slideQuiz.type !== "connect") {
            hideAnswer();
        }
    }

    submitQuiz({ timeout = false, noFeedback = false, disableRetakable = true, submitBtn } = {}) {
        const slide = this._slide;
        const slideQuiz = Q3.data.slides[slide].slideQuiz;

        const { slideQuizScore } = this._dom;

        let rewardMessage = null;
        let setNextSlide = true;
        let callbacks = [];
        let className;

        if (submitBtn && submitBtn.hasClass("disabled") && !submitBtn.hasClass("confirm")) {
            submitBtn.addClass("confirm")
            return;
        }

        if (submitBtn && submitBtn.hasClass("continue")) {
            setNextSlide = true;
            noFeedback = true;
            setNextSlide && goToNextVisibleSlide({ userAction: true, quizSubmit: true });
            MeshIsolation.end();
            return;
        }


        $('.overlay2d').removeClass('hide-labels');
        $('.hidden-by-quiz').removeClass('hidden-by-quiz');
        $('#quiz-box').removeClass('active');
        $('#quiz-box .not-required-msg').removeClass('active');
        $('#quiz-box .required-msg').removeClass('active');
        window.removeEventListener('keydown', dragByKey);
        resize();

        let input;

        if (!timeout) {
            if (slideQuiz.type === "radio" || slideQuiz.type === "bool") {
                input = $('input[name=slide-' + slide + '-quiz]:checked');
            }

            if (slideQuiz.type === "textarea" ||
                slideQuiz.type === "isolated") {
                input = $('input[name=slide-' + slide + '-quiz], textarea[name=slide-' + slide + '-quiz]');
            }
        } else input = { val: () => "timed out" };


        MeshIsolation.end();

        if (this.timer) {
            clearInterval(this._quizInterval);
            this.timer = 0;
        }

        if (slideQuiz.type !== "drag" &&
            slideQuiz.type !== "draglabels" &&
            slideQuiz.type !== "fraction" &&
            slideQuiz.type !== "plopQuiz" &&
            slideQuiz.type !== "connect" &&
            slideQuiz.type !== "ferris" &&
            slideQuiz.type !== "interactiveCar" &&
            slideQuiz.type !== "matrix" &&
            slideQuiz.type !== "dragDrop2d") {
            if (slideQuiz.type === 'radio') {
                slideQuiz.userAnswer = [...input].map(x => Number(x.value));
                /*if (!slideQuiz.radioQuiz.multipleCorrect) {
                    slideQuiz.userAnswer = slideQuiz.userAnswer[0];
                }*/
            } else if (slideQuiz.type === "select" || slideQuiz.type === "region") {
                slideQuiz.userAnswer = slideQuiz.userScore;
            } else if (slideQuiz.type === "ftb") {
                let correct = 0;
                let runAfterValid = [];
                slideQuiz.fillTheBlanksQuiz.blanks.filter(b => b.type === "input").forEach((b, i) => {
                    b.$el.attr("disabled", "");
                    let answerObj = b.answerObj;
                    let inputVal = b.$el.val().trim();
                    let validation = answerObj.validate(inputVal);
                    if (validation.valid) {
                        correct++;
                    }
                    b.$el.addClass(validation.valid ? "correct" : "wrong");
                });
                slideQuiz.userAnswer = true;
                slideQuiz.fillTheBlanksQuiz.blanks.filter(b => b.type !== "input").forEach((b, i) => {
                    if (b.userAnswer && b.userAnswer.index === i) {
                        b.$el.removeClass("active").addClass("correct");
                        correct++;
                    } else {
                        b.$el.removeClass("active").addClass("wrong");
                    }
                });
                runAfterValid.forEach(f => { f(); })
                slideQuiz.userScore = correct === slideQuiz.fillTheBlanksQuiz.blanks.length ? slideQuiz.score : 0;
            } else {
                slideQuiz.userAnswer = input.val() ? input.val().trim() : "";
            }
        }

        if (slideQuiz.type === "fraction") {
            let sumNumerator = 0;

            slideQuiz.userAnswer = slideQuiz.assessments.map(a => {
                let val = a.getValue();
                sumNumerator += val.numerator * (12 / val.denominator);
            });

            slideQuiz.userAnswer = String(sumNumerator);
        } else if (slideQuiz.type === "connect") {
            let score = Graph.getScore();

            slideQuiz.userScore = Math.round(score.score / slideQuiz.resultConnections.length * slideQuiz.score);
            slideQuiz.userAnswer = score.userInput;

            if (slideQuiz.userScore === slideQuiz.score) {
                slideQuiz.rightAnswer = className = "complete";
            } else {
                slideQuiz.userScore = 0;
            }
        } else if (slideQuiz.type === "dragDrop2d") {
            slideQuiz.userScore = getDragDrop2DScore(slideQuiz);
            slideQuiz.userAnswer = true;
        } else if (slideQuiz.type === "matrix") {
            slideQuiz.userScore = Object.keys(slideQuiz.userAnswer).length === slideQuiz.rightAnswer.length &&
                Object.keys(slideQuiz.userAnswer).every(a => slideQuiz.rightAnswer.indexOf(a) !== -1) ? slideQuiz.score : 0;
            Object.keys(slideQuiz.userAnswer).forEach(a => {
                let col = +a.split(" ")[0];
                let row = +a.split(" ")[1];
                let el = $(`.matrix-quiz table tr:nth-child(${row + 2}) td:nth-child(${col + 2})`);
                el.css("background", slideQuiz.rightAnswer.indexOf(a) === -1 ? "red" : "green");
            })
        }


        if (typeof slideQuiz.userAnswer === "string") {
            slideQuiz.userAnswer = slideQuiz.userAnswer.toLowerCase();
        }

        if (slideQuiz.type === "drag") {
            let arr = [];

            for (let i = 0; i < $('ul.draggable-cnt li').length; i++) {
                let el = $($('ul.draggable-cnt li')[i]);
                arr.push(el.data('answer').toString());
            }

            slideQuiz.userAnswer = JSON.stringify(arr);
        }

        if (slideQuiz.type === "draglabels") {
            let correctAnswers = 0;
            let length = Object.keys(slideQuiz.labels).length;

            for (let key in slideQuiz.labels) {
                let label = Q3.labels.live.getLabelByUuid(key);

                if (slideQuiz.labels[key].userAnswer === slideQuiz.labels[key].name) {
                    label._element.domElement.classList.add("correct");
                    correctAnswers++;
                } else {
                    label._element.domElement.classList.add("wrong");
                }
            }
            $(".answer.ui-draggable").draggable("disable");
            $(".answer-placeholder .reset-btn").remove();

            if (correctAnswers === length) {
                slideQuiz.rightAnswer = slideQuiz.userAnswer = className = "complete";
            } else {
                slideQuiz.userAnswer = slideQuiz.userScore = 0;
            }
        } else if (slideQuiz.type === "plopQuiz") {
            const maxScore = slideQuiz.score;
            const length = slideQuiz.assessments.length;
            slideQuiz.userScore = 0;

            const allCorrect = slideQuiz.partialCredit || slideQuiz.assessments.reduce((allCorrect, ass) => allCorrect && ass.isCorrectlyPlopped, true);

            slideQuiz.assessments.forEach((ass, i) => {
                const labelScore = (ass.isCorrectlyPlopped && allCorrect)
                    ? i === length - 1
                        ? maxScore - Math.round(maxScore / length) * i
                        : Math.round(maxScore / length)
                    : 0;
                slideQuiz.userScore += labelScore;

                const mesh = slideQuiz.meshes[i];

                mesh.score = labelScore;
                mesh.solved = true;
                mesh.name = getMeshDisplayName(mesh.uuid);
                mesh.finalMatrix = ass.object.matrix.clone();

                ass.setSolved(labelScore > 0);
            });

            if (slideQuiz.userScore > 0) {
                slideQuiz.rightAnswer = slideQuiz.userAnswer = "complete";
            } else {
                slideQuiz.rightAnswer = 100;
                slideQuiz.userAnswer = slideQuiz.userScore = "0";
            }
            // Q3.AssessmentManager.clearAssessments();
        }

        if (slideQuiz.type === "connect"
            || slideQuiz.type === "matrix"
            || slideQuiz.type === "dragDrop2d"
            || slideQuiz.type === "select"
            || slideQuiz.type === "region"
            || slideQuiz.type === "ftb") {
            className = slideQuiz.userScore === slideQuiz.score ? "complete" : "fail";
        } else if (slideQuiz.type === "textarea") {
            let userAnswer = slideQuiz.userAnswer.toLowerCase();
            let rightAnswer = slideQuiz.rightAnswer.toLowerCase().split(';');
            let wrong = -1;
            for (let i of rightAnswer) {
                wrong = userAnswer.indexOf(i);
                if (wrong !== -1) break;
            }
            className = wrong === -1 ? "fail" : "complete";
        } else if (slideQuiz.type === 'radio') {
            let { answers, multipleCorrect } = slideQuiz.radioQuiz;
            className = 'complete';
            let userAnswer = new Set(slideQuiz.userAnswer);
            for (let [answerIndex, answer] of answers.entries()) {
                if (userAnswer.has(answerIndex)) {
                    answer.$el.addClass(`selected ${answer.correct ? "correct" : "wrong"}`);
                    if (!answer.correct) {
                        className = 'fail';
                    }
                } else if (answer.correct) {
                    className = 'fail';
                }
            }
        } else {
            className = slideQuiz.rightAnswer.toString().toLowerCase() === slideQuiz.userAnswer.toString().toLowerCase()
                ? "complete"
                : "fail";
        }
        if (input && input.addClass) {
            input.addClass(className === "complete" ? "correct" : "wrong");
        }

        let feedbackTypes = ["radio", "bool"];
        if (feedbackTypes.indexOf(slideQuiz.type) !== -1 && slideQuiz.answerBasedFeedbackEnabled) {
            let feedbackIndex;
            if (slideQuiz.type === "radio") {
                feedbackIndex = slideQuiz.userAnswer[0]
            }
            if (slideQuiz.type === "bool") {
                feedbackIndex = ["true", "false"].indexOf(slideQuiz.userAnswer)
            }
            if (feedbackIndex !== -1 && slideQuiz.answerBasedFeedbacks[feedbackIndex]) {
                rewardMessage = slideQuiz.answerBasedFeedbacks[feedbackIndex];
            }
        }

        if (slideQuiz.type === "fraction") {
            let userAnswer = simplifyFraction(Number(slideQuiz.userAnswer), 12);

            setNextSlide = true;
            rewardMessage = className === "complete"
                ? null
                : $(`<span><span>Sorry, your pizza says: </span> ${createFractionDom(userAnswer.numerator, userAnswer.denominator)} </span>`).html();

            callbacks.push(function () {
                removePieAssessment(slideQuiz.assessments, slideQuiz.objects);
                slideQuiz.assessments = [];
                slideQuiz.objects = [];
            });
        } else if (slideQuiz.type === "plopQuiz") {
            setNextSlide = true;
            callbacks.push(function () {
                // slideQuiz.assessments = [];
                slideQuiz.objects = [];
            });

            if (!slideQuiz.isRetakable) {
                callbacks.push(function () {
                    AssessmentManager.clearAssessments();
                });
            }
        } else if (slideQuiz.type === "ferris") {
            setNextSlide = true;
        }

        if (className === "complete") {
            if (slideQuiz.type === "isolated") {
                slideQuiz.meshes[0].score = slideQuiz.score;
                slideQuiz.meshes[0].userAnswer = slideQuiz.userAnswer;
            }

            if (slideQuiz.type !== "plopQuiz" &&
                slideQuiz.type !== "draglabels" &&
                slideQuiz.type !== "connect" &&
                slideQuiz.type !== "dragDrop2d") {
                slideQuiz.userScore = slideQuiz.score;
            }
            this.score += slideQuiz.userScore;
            slideQuizScore.html(slideQuiz.userScore);

            if (slideQuiz.isInteractive) {
                AssessmentManager.inAssessmentMode = false
                this._dom.quizWrap.hide();
                slideQuiz.actions.filter(a => a.userAction === "quiz_success").forEach(currAction => {
                    const transition = new Transition(currAction.systemAction, currAction.value, this);
                    transition.click();
                });
                return;
            } else {
                if (!noFeedback) {
                    let assessment = slideQuiz.correctAssessment;
                    if (assessment.targetSlide !== null) {
                        setNextSlide = false;
                        callbacks.push(() => {
                            SlideManager.setSlide(assessment.targetSlide);
                        })
                    }
                    $(".submit-cnt").css({ opacity: 0, pointerEvents: "none" });
                    callbacks.push(() => {
                        slideQuiz.isSubmitted = "correct";
                        setNextPrevEnabled({ prev: true, next: true });
                        $(".submit-cnt").css({ opacity: 0, pointerEvents: "none" });
                    })
                    RewardScreens.showRandomReward({
                        message: assessment.solutionEnabled && assessment.solutionText ? assessment.solutionText : null,
                        setNextSlide: setNextSlide,
                        callbacks,
                        playAudio: assessment.audioEnabled,
                        audioPath: assessment.audioUuid && Q3.data.assets.audio[assessment.audioUuid] ? Q3.data.assets.audio[assessment.audioUuid].path : false
                    })
                }
            }


            this.answers++;
        } else {
            if (slideQuiz.type === "isolated") {
                slideQuiz.meshes[0].score = 0;
                slideQuiz.meshes[0].userAnswer = slideQuiz.userAnswer;
            }

            if (!disableRetakable && slideQuiz.isRetakable !== false) {
                delete slideQuiz.userAnswer;
                delete slideQuiz.userScore;
                setNextSlide = true;
            }
            if (slideQuiz.isInteractive) {
                AssessmentManager.inAssessmentMode = false
                this._dom.quizWrap.hide();
                slideQuiz.actions.filter(a => a.userAction === "quiz_failure").forEach(currAction => {
                    const transition = new Transition(currAction.systemAction, currAction.value, this);
                    transition.click();
                });
                return;
            } else {
                if (!noFeedback) {
                    let assessment = slideQuiz.incorrectAssessment;
                    if (assessment.targetSlide !== null) {
                        setNextSlide = false;
                        callbacks.push(() => {
                            SlideManager.setSlide(assessment.targetSlide);
                        })
                    }

                    $(".submit-cnt .submit-btn").css({ opacity: 0, pointerEvents: "none" });
                    callbacks.push(() => {
                        slideQuiz.isSubmitted = "wrong";
                        setNextPrevEnabled({ prev: true, next: true });
                        this.initSolution(slideQuiz, $(".submit-cnt"))
                    })
                    RewardScreens.showRandomFailure({
                        message: assessment.solutionEnabled && assessment.solutionText ? assessment.solutionText : null,
                        setNextSlide: false,
                        callbacks,
                        playAudio: assessment.audioEnabled,
                        audioPath: assessment.audioUuid && Q3.data.assets.audio[assessment.audioUuid] ? Q3.data.assets.audio[assessment.audioUuid].path : false
                    })
                }
                this.answers++;
            }
        }

        // if (rewardMessage !== null) {
        //     let messageHtml = $('<span></span>').text(rewardMessage).html();
        //     let feedback = $('<div class="quiz-feedback">' + messageHtml + '</div>');
        //     let feedbackCnt = $('.quiz-cnt .feedback-cnt');
        //     feedbackCnt.append(feedback);
        // }
        Analytics.add({
            type: "quiz-action",
            eventName: "quiz-submit",
            answer: slideQuiz.userAnswer,
            is_correct: className === "complete"
        });

        const getClassName = (el) => {
            if (slideQuiz.type === "radio") {
                return slideQuiz.radioQuiz.answers[el.value].correct ? "correct" : "wrong";
            } else if (el) {
                return el.value.toString().toLowerCase() === slideQuiz.rightAnswer.toString().toLowerCase() ? "correct" : "wrong"
            }
        };
        $('input[name="slide-' + slide + '-quiz"]').off().each((i, el) => {
            $(el).attr("disabled", "disabled")
        });
        let checked = $('input[name="slide-' + slide + '-quiz"]:checked');
        if (slideQuiz.type !== "radio") {
            checked.parent().addClass(getClassName(checked[0]));
        }
        // if (!(className === "fail" && !disableRetakable && slideQuiz.isRetakable !== false)) {

        submitBtn && submitBtn.addClass("continue").html(this.translation.continue);


        if (slideQuiz.type === "drag") {
            dragSortable.option('disabled', true);

            let answers = JSON.parse(Q3.data.slides[slide].slideQuiz.rightAnswer);
            let answerIndices = new Map();
            for (let [i, answer] of answers.entries()) {
                answerIndices.set(answer, i);
            }
            let items = $('.quiz-cnt ul li').off();
            items.each((i, item) => {
                let distance = Math.abs(answerIndices.get($(item).data('answer').toString()) - i);
                if (distance === 0) {
                    $(item).addClass('correct');
                } else {
                    $(item).addClass('wrong');
                }
            });
        } else if (slideQuiz.type === 'textarea') {
            let answerCnt = $('.quiz-cnt .answer-cnt');
            answerCnt.addClass(className === 'complete' ? 'correct' : 'wrong');
            answerCnt.find('textarea').prop('readonly', true);
        }
        slideQuiz.isSubmitted = true;
        // $(".submit-cnt").css({ opacity: 0, pointerEvents: "none" });
        if (slideQuiz.type === "ftb" && slideQuiz.fillTheBlanksQuiz.ftbType === "capsule") {
            slideQuiz.fillTheBlanksQuiz.blanks.forEach(b => {
                if (b.userAnswer) {
                    b.$el.html(b.userAnswer.text)
                    b.userAnswer.$el.css({ opacity: 0 });
                }
            })
        }
        slideQuiz.submittedDom = this._dom.quizCnt[0].innerHTML;
        // }

        if (slideQuiz.type === "plopQuiz" || slideQuiz.type === "draglabels") {
            setTimeout(() => replaceWithRevealButton(slideQuiz, submitBtn), 10); // delayed because jquery is crappy and triggers click for this object even with prevent default enabled
        }
    }

    showRateScreen() {

        let results = this.quizCount > 0 ? this.showFinalScore() : false;

        $("#chartjs-tooltip").remove();

        $("#rate-screen").addClass("active");

        Analytics.finishTour(results);

        SlideManager.activeSlideAudio.pause();
        Q3.backgroundAudio.pause();
    }

    showFinalScore() {

        let titles = [
            "Excellent work!",
            "Well done!",
            "Not bad!",
            "Poor!",
            "Keep trying your best!"].reverse();

        let titleEl = $('#quiz-score-title');
        let scoreEl = $('<div class="score-el"></div>');
        let quizList = $('<div class="quiz-list"></div>');
        let finalModal = $('<div id="final-score" class="final-score"></div>');
        let timeSpent = Math.max(0, Analytics.getTimeSpent());
        let timeEl = $('<div class="time-el">Time spent: <b style="color: #f4f67c">' +
            Utils.secondsToString(Math.round(timeSpent / 1000)) + '</b>.</div>');

        let submitBtn = $('#submit-score-btn');

        submitBtn.show();

        finalModal.append(scoreEl, quizList, timeEl);

        let quizData = Q3.data.quizData;
        let slideQuizes = {};

        Q3.data.slides.filter(s => s.visible !== false).forEach((slide, i) => {
            if (slide.slideQuiz && slide.slideQuiz.active) {
                slideQuizes[i] = slide.slideQuiz;
            }
        });

        let userScore = 0;
        let totalQuizes = 0;
        let answeredQuizes = 0;
        let maxScore = 0;
        let questionKey = "question";

        generateFinalScoreList(slideQuizes);

        questionKey = "name";
        generateFinalScoreList(quizData);

        // titleEl.html(titles[Math.round(answeredQuizes / totalQuizes * (titles.length - 1))]);
        scoreEl.html('Total score: <b style="color: #f4f67c" >' + userScore + '</b> (' +
            answeredQuizes + ' right answers out of ' + totalQuizes + ')');

        // let slideQuizesData = [];
        // Q3.data.slides.filter(s => s.visible !== false).forEach(function (slide) {
        //     slideQuizesData.push(slide.slideQuiz);
        // });
        //
        // let resultData = {
        //     slideQuiz: JSON.stringify(slideQuizesData),
        //     labelQuiz: JSON.stringify(Q3.data.quizData),
        //     score: userScore,
        //     time: timeSpent,
        //     wrongAnswers: totalQuizes - answeredQuizes,
        //     correctAnswers: answeredQuizes,
        //     maxScore: maxScore
        // };

        submitBtn.click(() => {
            if (Q3.student || window.LTI_callback) {
                let API_URL = window.API_URL ? window.API_URL : `${window.location.origin}/api`;
                let data = {};

                if (Q3.student) {
                    data.student = Q3.student.id;
                    data.class = Q3.student.classId;
                }

                if (window.LTI_callback) {
                    data.LTI_callback = window.LTI_callback;
                }
                if (window.LTI_sourceid) {
                    data.LTI_sourceid = window.LTI_sourceid;
                }
                if (window.LTI_key) {
                    data.LTI_key = window.LTI_key;
                }

                $.ajax({
                    url: API_URL + '/result',
                    type: 'POST',
                    data: data,
                    success: function () {
                        console.log('uploaded quizData');
                    }
                });
            }
        });

        // $("#quiz-final-result").empty().append(finalModal);

        function generateFinalScoreList(quizData) {
            Object.keys(quizData).forEach(key => {
                let q = quizData[key];

                if (q.active) {
                    maxScore += q.score;

                    let quiz = {
                        question: q[questionKey],
                        score: q.userScore ? Number(q.userScore) : 0,
                        answered: "fail",
                    };

                    if (["select", "isolated", "draglabels", "plopQuiz", "region"].indexOf(q.type) !== -1) {
                        const type = q.type === "draglabels" ? "labels" : "meshes";

                        Object.keys(q[type]).forEach(key => {
                            const data = q[type][key];

                            let className = "fail";

                            if (data.score > 0) {
                                className = "complete";
                                answeredQuizes++;
                                userScore += data.score;
                            }

                            let question = data.question
                                ? data.question
                                : (q.type === "draglabels" ? "Drag " : "Find ") + data.name;

                            quizList.append('<div class="quiz-list-item ' + className + '">' +
                                question + '<span class="score">' +
                                (data.score || 0) + '</span></div>');

                            totalQuizes++;
                        });

                    } else {
                        totalQuizes++;

                        if (quiz.score > 0) {
                            userScore += quiz.score;
                            answeredQuizes++;
                            quiz.answered = "complete";
                        }

                        quizList.append('<div class="quiz-list-item ' + quiz.answered + '">' +
                            quiz.question + '<span class="score">' + (quiz.score || 0) +
                            '</span></div>');
                    }
                }
            });
        }

        // return resultData;
    }

    addQuizbox() {
        const quizBox = document.createElement('div');

        quizBox.id = 'quiz-box';
        quizBox.className = 'quiz-box';
        quizBox.innerHTML = '';

        const quizReqCnt = document.createElement('div');
        quizReqCnt.className = "msg-cnt required-msg";
        quizReqCnt.innerHTML = "<p><b>Wait!</b></p><p>There is a required quiz that you didn't answer yet. </p>";

        const quizReqOkBtn = document.createElement('div');
        quizReqOkBtn.className = "quiz-msg-btn";
        quizReqOkBtn.innerHTML = "Okay";

        quizReqOkBtn.addEventListener("click", function (e) {
            e.stopImmediatePropagation();
            quizBox.className = 'quiz-box';
            quizReqCnt.classList.remove('active');
            stopAutoplay();
        });

        quizReqCnt.appendChild(quizReqOkBtn);

        const quizNotReqCnt = document.createElement('div');
        quizNotReqCnt.className = "msg-cnt not-required-msg";
        quizNotReqCnt.innerHTML = "<p><b>Slow down!</b></p><p>There is a quiz that you didn't answer yet. Do you want to continue to the next slide?</p>";

        const quizNotReqYesBtn = document.createElement('div');
        quizNotReqYesBtn.className = "quiz-msg-btn";
        quizNotReqYesBtn.innerHTML = "Next Slide";

        const quizNotReqNoBtn = document.createElement('div');
        quizNotReqNoBtn.className = "quiz-msg-btn";
        quizNotReqNoBtn.innerHTML = "Stay";

        quizNotReqNoBtn.addEventListener("click", function (e) {
            e.stopImmediatePropagation();
            quizBox.className = 'quiz-box';
            quizNotReqCnt.classList.remove('active');
            stopAutoplay();
        });

        quizNotReqYesBtn.addEventListener("click", function (e) {
            e.stopImmediatePropagation();
            if (Q3.QuizManager.timer) Q3.QuizManager.submitQuiz({ timeout: true, noFeedback: true });
            goToNextVisibleSlide({ ignore: true, userAction: true });
            quizBox.className = 'quiz-box';
            quizNotReqCnt.classList.remove('active');
        });

        quizNotReqCnt.appendChild(quizNotReqNoBtn);
        quizNotReqCnt.appendChild(quizNotReqYesBtn);

        quizBox.appendChild(quizReqCnt);
        quizBox.appendChild(quizNotReqCnt);

        if (!Q3.editMode) {
            Q3.container.parentElement.appendChild(quizBox);

            window.addEventListener('keydown', (e) => {
                if (e.key !== "Enter") {
                    return;
                }
                if (quizNotReqCnt.classList.contains("active")) {
                    quizNotReqYesBtn.click();
                }
                if (quizReqCnt.classList.contains("active")) {
                    quizReqOkBtn.click();
                }
            });

        }

        this._dom.quizBox = quizBox;
        this._dom.quizReqCnt = quizReqCnt;
        this._dom.quizNotReqCnt = quizNotReqCnt;

        return quizBox;
    }

    appendRateScreen() {
        if (Q3.editMode) return;

        let rateCnt = $('<div id="rate-screen"><div id="quiz-score-title"></div></div>');
        let flex = $('<div class="d-flex flex-grow flex-wrap"><div id="quiz-final-result"></div></div>');
        let rateScreen = $(`<div class="rate-screen"><div class="logo-holder"><h4>${this.translation.createdBy}</h4></div></div>`);

        flex.append(rateScreen);

        let section = $(`<section class="presentation-holder"><div class="logo-cnt"><img class="logo" src="/images/logo/Embibe-new-Logo-sm.png"></div><h5>${this.translation.rate}:</h5></section>`);
        let rates = $('<div class="col-holder"></div>');

        for (let i = 1; i <= 5; i++) {
            let rate = $('<div class="col"><span></span></div>');
            let stars = $('<div class="stars"></div>');

            for (let s = 1; s <= i; s++)
                stars.append('<img src="/images/icons/star.svg">');

            rate.prepend(stars);
            rate.click(function () {
                rate.addClass("active").siblings().removeClass("active");
            });

            rates.append(rate);
        }

        let buttons = $('<div id="rate-screen-buttons"></div>');
        let submitBtn = $(`<div id="submit-score-btn" tabindex="${getTabIndex()}" class="btn" style="display: none">SUBMIT Score</div>`);
        let watchAgain = $(`<div class="btn-holder" tabindex="${getTabIndex()}"><a class="btn">${this.translation.watchAgain}</a></div>`);
        let goBack = $(`<div class="btn-holder" tabindex="${getTabIndex()}"><a class="btn">Back to last slide</a></div>`);

        goBack.click(() => {
            $("#rate-screen").removeClass("active");
            SlideManager.setSlide(SlideManager.activeSlide, { force: true });
        });

        watchAgain.click(() => window.location.reload());

        let otherToursLink = $(`<a href="${window.location.origin}/library" tabindex="${getTabIndex()}" class="btn-explore hidden-mobile">Explore other tours</a>`);
        let analyticsBtn = $(`<div class="w-100 hidden-mobile"><a target="_blank" class="analytics-btn" id="analytics-btn" href="#">Analytics report</a></div>`);

        // let mobileButtons = $('<div class="hidden-desktop hidden-tablet" style="order: 4; text-align: center"></div>');

        // buttons.append(goBack, submitBtn, watchAgain, otherToursLink, analyticsBtn);
        buttons.append(watchAgain);
        // mobileButtons.append(otherToursLink.clone().removeClass("hidden-mobile"));
        // mobileButtons.append(analyticsBtn.clone().removeClass("hidden-mobile"));
        flex.append(buttons);
        rateScreen.append(section.append(rates));

        const splash = rateCnt.append(flex);

        $(Q3.container.parentElement).append(rateCnt.append(flex));

        return splash;
    }

    updateQuizWrap() {
        const posKey = getVWKey() === "sizeSm" ? "positionSm" : "position";
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (!slideQuiz || !slideQuiz.active) return;
        if (slideQuiz && slideQuiz[posKey]) {
            const quizWrap = this._dom.quizWrap;

            quizWrap.css("left", slideQuiz[posKey].x + "%");
            quizWrap.css("top", slideQuiz[posKey].y + "%");
        }
        awaitLib().then(() => {
            MathJax.typesetPromise([this._dom.quizCnt[0]]);
        });
    }

    createQuizWrap() {
        let quizWrap = $('<div id="slide-quiz-wrap" class="quiz-wrap"></div>');
        let scoreEl = $('<div class="score"></div>');

        quizWrap.prepend(scoreEl);

        if (Utils.isMobile.any()) {
            //to close keyboard
            quizWrap.css('pointer-events', 'all');
        }

        let quizCnt = $('<div id="slide-quiz" class="slide-quiz quiz-cnt"></div>');
        let slideQuizScoreCnt = $('<div id="slide-quiz-score" class="livelabel"></div>');
        let slideQuizScoreEl = $('<div class="quiz-button question-hidden"></div>');
        let slideQuizScore = $('<div class="quiz-score"></div>');

        quizWrap.append(quizCnt);

        let timeEl = $('<div class="time-limit"></div>');

        quizWrap.append(timeEl);

        let mobileQuizCnt = $('<div id="mobile-quiz-wrap" class="quiz-wrap"></div>');

        slideQuizScoreCnt.append(slideQuizScoreEl.append(slideQuizScore));

        let hintsCnt = $('<div class="hints-cnt mobile"></div>');
        let hintsContent = $('<div class="hints-content"></div>');
        let hintControls = $('<div class="hints-controls"></div>');
        let back = $('<div class="close"></div>');
        back.click(() => {
            hintsCnt.removeClass("active");
        })
        let moreHints = $('<button class="quiz-msg-btn hints-btn transparent">More Hints</button>');
        hintControls.append(back, moreHints)
        hintsCnt.append(hintsContent, hintControls)

        this.setSlideQuizElements({
            quizWrap,
            quizCnt,
            scoreEl,
            slideQuizScoreEl,
            slideQuizScore,
            timeEl,
            hintsCnt,
            hintsContent
        });

        return { quizWrap, slideQuizScoreCnt, mobileQuizCnt };
    }

    get questionIframeOrigin() {
        return 'https://d31fojhwz0gmor.cloudfront.net'
    }

    getEmbibeQuestionIframeUrl(id) {
        const questionIframeUrl = `${this.questionIframeOrigin}/iframe/liveclass/`;
        const quizSessionId = Utils.queryParams.get("quiz_sessionId")
        const token = Utils.queryParams.get("embibe-token")
            || Utils.getCookie("preprod_embibe-token")
            || Utils.getCookie("school_preprod_embibe-token")
            || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJjb3VudHJ5IjoxLCJ1c2VyX3R5cGUiOjMsImNyZWF0ZWQiOjE2Mzc3NTExNzEsIm9yZ2FuaXphdGlvbl9pZCI6IjYwYzFlM2QxMzM1ZjVhNWQ0NDZmNmJjZSIsImlkIjoxNTAwMDQ1MDEyLCJleHAiOjE2Mzc5MjM5NzEsImRldmljZUlkIjoiMC42NDc3MzIwOTM3MTA2MDE5IiwibW9iaWxlX3ZlcmlmaWNhdGlvbl9zdGF0dXMiOmZhbHNlLCJlbWFpbF92ZXJpZmljYXRpb25fc3RhdHVzIjp0cnVlLCJlbWFpbCI6InJhamVzaGtAZW1iaWJlLmNvbSJ9.2yBmgSsa0N4vbi31DArg1aN9XCQy_eCjof5peCJqO-SM11Ccvx5d3rlriie-9PpE0N7FmupykWdeH81p0ylKCQ';
        const sessionId = Q3.sessionId || Q3.data._id;
        const paramsObj = {
            token: token,
            mode: "AdhocPractice",
            title: "",
            conceptCode: "",
            questionCode: id,
            goal_name: "CBSE",
            exam_name: "10th%20CBSE",
            contextType: "Teach",
            source: "Teach",
            contextId: sessionId,
            classView: "student",
        }
        if (quizSessionId) {
            paramsObj.sessionId = quizSessionId;
        }
        if (Q3.projectorMode) {
            paramsObj.projectorMode = true;
        }
        const params = new URLSearchParams(paramsObj).toString();
        return `${questionIframeUrl}?${params}`
    }
}

export default QuizManager;
export { QuizManager };


function initEmbibeQuizIframe(quizWrap, slideQuiz) {
    const id = slideQuiz.embibeQuestionId;
    const questionIframeUrl = Q3.QuizManager.getEmbibeQuestionIframeUrl(id);
    quizWrap.html(`<iframe
      class="slidequiz-iframe"
      src="${questionIframeUrl}"
      frameborder="0"
    />`)
    quizWrap.show()
    Q3.container.appendChild(quizWrap[0]);

    window.addEventListener('message', (e) => {
        console.log(e);
        if (e.origin === questionIframeOrigin && e.data.type === 'get_event') {
            let eventName;
            let extraParams;
            try {
                eventName = e.data.data.data.event_name;
                extraParams = e.data.data.data.extra_params;
            } catch (err) {
                console.log(err.message || err);
            }
            if (eventName === 'PQ_END_Practice_Click' || eventName === 'PQ_CONT_Click') {

            }
            if (eventName === 'PQ_CHK_AN_Click') {
                slideQuiz.isSubmitted = true;
                RewardScreens[extraParams.is_correct ? "showRandomReward" : "showRandomFailure"]({
                    message: null,
                    setNextSlide: true,
                    callbacks: [],
                    playAudio: false,
                    audioPath: false
                })
            }
        }

    });


}

function initEmbibeQuiz({ quizWrap, quizCnt, slide, scope }) {
    quizWrap.empty().addClass("embibe-quiz");
    let wrap = $(`<div class="practice-test-wrapper"></div>`);
    let wrapInner = $(`<div class="practice-question-answer-wrapper"></div>`);
    quizWrap.append(wrap.append(wrapInner));

    // $.ajax({
    //     url: "https://api.embibe.com/practice_ms/v1/practice/5d5beb4cb131c500016cdeb7/question?",
    //     type: 'GET',
    //     dataType: 'json',
    //     headers : {
    //         'browser-id': '1566370603732',
    //         'embibe-token': "eyJhbGciOiJIUzUxMiJ9.eyJpZCI6MTIwMDc3ODYxMywiZW1haWwiOiJndWVzdF8xNTY2MzA1MDk5NDQyNDkyQGVtYmliZS5jb20iLCJpc19ndWVzdCI6dHJ1ZSwicm9sZSI6InN0dWRlbnQiLCJ0aW1lX3N0YW1wIjoiMjAxOS0wOC0yMFQxMjo0NDo1OS43OTRaIn0.6UZOYn3GYUnx5GWOjmoIEFjGkg3Uy72HxJaQJm4rD9zHXPWcRXPFNSI5P82Do1Af_oLnQJkujuRSCTFxUQ9LjQ",
    //     },
    //     success: function (data) {
    //         console.log(data);
    //         fillQuiz(Object.values(data.result)[0]);
    //     }
    // });

    fillQuiz(Object.values(embibeTempQuestion.result)[0]);
    function fillQuiz(questionData) {
        let header = $(`<div class="practice-header-mobile">
            <div class="practice-header-wrapper">
                <div class="question-heading">
                    <div class="header-left-components">
                        <div class="question-heading__type">${questionData.category} Question</div>
                    </div>
                    <div class="header-right-components">
                        <div class="bookmark">
                            <div class="bookmark-logo inline-flex">
                                <img src="https://assets.embibe.com/staging/assets_new/images/divum/images/bookmark.svg"
                                     alt="bookmark" class="sprite-image">
                            </div>
                        </div>
                        <div class="timer" style="background-color: rgba(241, 102, 97, 0);">
                            00:00:00
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `);

        let body = $(`<div class="test-wrapper">
            <div class="question-options-container">
                <div class="question-container">
                    <div class="-container">
                        <div class="-body">${questionData.body}${questionData.tags.map(t => `<span class="question-special-tag">${t}</span>`).join("")}</div>
                    </div>
                </div>
                <div class="options-container background-white">
                    <div class="mcq-container ">
                    ${questionData.answers.map((a, i) => `<label class="${a.code}">
                            <input type="radio" name="quiz" class="embibe-quiz-radio" value="${a.code}">
                            <div class="answer-mcq-wrapper answer-clikable">
                                <div class="answer-mcq-wrapper__name ">${String.fromCharCode(i + "a".charCodeAt(0)).toUpperCase()}</div>
                                <div class="answer-mcq-wrapper__text">${a.body}</div>
                            </div>
                        </label>`).join("")}
                    </div>
                </div>
            </div>
        </div>
        `);
        let solutionWrapper = $(`<div class="solution-wrapper minimize"></div>`);
        let footer = $(`<div class="test-footer-answer">
            <div class="outer-practice-footer-wrapper">
                <div class="question-footer-wrapper background-color-lightgrey3">
                    <div class="button-skip-box">
                        <button class="btn button-skip">Skip</button>
                    </div>
                    <div class="button-check-box">
                        <button class="btn button-check disable-button" >Check
                        </button>
                    </div>
                    <div class="button-continue-box" style="display: none">
                        <button class="btn button-continue button-continue-box-active" >Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>`);
        wrapInner.append(header, body, solutionWrapper, footer);

        QuizManager.timer = 0;
        let timeEl = $(".practice-header-mobile .timer");
        timeEl.html(Utils.secondsToString(QuizManager.timer, true));
        clearInterval(QuizManager._quizInterval);
        QuizManager._quizInterval = setInterval(function () {
            QuizManager.timer++;
            timeEl.html(Utils.secondsToString(QuizManager.timer, true));

            if (QuizManager.timer > questionData.ideal_time) {
                timeEl.css("background-color", "#f16661")
            }
        }, 1000);

        let selectedAnswer = false;
        $('.embibe-quiz-radio').on('input', (e) => {
            selectedAnswer = e.currentTarget.value;
            $(".practice-test-wrapper .btn.button-check")
                .removeClass('disable-button')
                .addClass("button-check-box-active")
        });
        $(".practice-test-wrapper .btn.button-check").on("click", (e) => {
            if (!selectedAnswer) return;
            $('.embibe-quiz-radio').attr('disabled', true);
            $('.test-wrapper .mcq-container > label').addClass('disabled');
            let explanationData = Object.values(embibeTempExplaination.result)[0];
            let explanation = "";
            let conceptsData = explanationData.answer_explanation.concepts_data;
            let answers = explanationData.answer_explanation.answers;
            for (let i = 0; i < answers.length; i++) {
                let a = answers[i];
                if (a.correct) {
                    $(`.test-wrapper .mcq-container .${a.code}`).addClass("correct-answer");
                    explanation = a.explanation.en; // Q3.language probably will be here later
                    if (selectedAnswer !== a.code) {
                        $(`.test-wrapper .mcq-container .${selectedAnswer}`).addClass("wrong-answer");
                        footer.addClass("incorrect");
                        $('.button-continue-box').prepend(`<span class="test-message">Incorrect</span>`)
                    } else {
                        footer.addClass("correct");
                        $('.button-continue-box').prepend(`<span class="test-message">Correct</span>`)
                    }
                    $('.button-skip-box, .button-check-box').hide();
                    $('.button-continue-box').show();
                    break;
                }
            }


            solutionWrapper.append($(`<div class="question-answer-solution-wrapper">
                <div class="solution-header">
                    <div class="solution-header-title">Solution</div>
                    <div class="solution-header-images">
                        <div class="solution-control maximize-half" data-state="maximize-half">
                            <div class="maximize-ui-button"></div>
                        </div>
                        <div class="solution-control compress" data-state="maximize-half">
                            <img
                                src="https://assets.embibe.com/staging/assets_new/images/divum/images/solution-compress.svg"
                                alt="expand" class="sprite-image">
                        </div>
                        <div class="solution-control maximize-full" data-state="maximize-full">
                            <img
                                src="https://assets.embibe.com/staging/assets_new/images/divum/images/solution-minimize-image.svg"
                                alt="expand" class="sprite-image">
                        </div>
                        <div class="solution-control minimize" data-state="minimize">
                            <img
                                src="https://assets.embibe.com/staging/assets_new/images/divum/images/solution-minimize.svg"
                                alt="expand" class="sprite-image">
                        </div>
                    </div>
                </div>
                <div class="solution-body solution-body-minimize-height">
                    <div class="solution-content-wrapper">
                        <div class="content-card">
                            <div class="solution-title">Explanation</div>
                            <div>
                                <div class="explanation-answers">
                                ${explanation}
                                </div>
                            </div>
                        </div>
                        <div class="key-concepts content-card">
                            <div class="solution-title">Key Concepts</div>
                            <div class="key-concepts-container">
                                ${conceptsData.map(c => `<a href="${c.url}" target="_blank"
                                   class="learn-concept-link">
                                    <div class="card-wrapper">
                                        <div class="card-title-wrapper">
                                                <div class="card-title toolTip-parent">${c.name}</div>
                                                <!--<div class="question-special-tag">Primary Concept</div>-->
                                            <img
                                                src="https://assets.embibe.com/staging/assets_new/images/arrow-right@2x.png"
                                                class="arrow-img">
                                        </div>
                                        <div class="card-content less-view" style="display: none">
                                            ${c.description}
                                        </div>
                                    </div>
                                </a>`)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            `));
            $(".practice-test-wrapper .solution-wrapper .solution-control").click((e) => {
                solutionWrapper[0].className = "solution-wrapper";
                solutionWrapper.removeClass().addClass("solution-wrapper").addClass(e.currentTarget.getAttribute("data-state"))
            });
            setTimeout(() => {
                solutionWrapper[0].className = "solution-wrapper maximize-half";
            }, 100)

        });
        // quizWrap.draggable({
        //     containment: "#viewer",
        //     handle: ".question-heading, .question-container",
        //     scroll: false
        // });
        MathJax.texReset();
        MathJax.typesetClear();
        MathJax.typesetPromise([solutionWrapper[0]]).catch(function (err) {
            // console.error(err);
            console.log(err.message);
        }).then(() => {
        });
        // MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
    }

}

function shuffleArray(arr) {
    let rndArr = arr.slice(0);
    for (let i = rndArr.length - 1; i >= 0; i--) {
        let j = Math.floor(Math.random() * (i + 1));
        [rndArr[i], rndArr[j]] = [rndArr[j], rndArr[i]];
    }
    return rndArr;
}

function createFractionDom(n, d) {
    return `<span class="mq-math-mode flex-grow"><span class="mq-fraction mq-non-leaf"><span class="mq-numerator">${n}</span><span class="mq-denominator">${d}</span></span></span>`;
}

function initBool({ quizCnt, slide, submitBtn, slideQuiz }) {
    submitBtn.addClass("disabled");
    let boolChoices = Object.keys(JSON.parse(slideQuiz.boolChoices));
    const yes = $(`<label data-answer="true" class="q-label" tabindex="${getTabIndex()}"><input name="slide-${slide}-quiz" type="radio" value="true"><span></span>${translations[Q3.cooboLang][boolChoices[0].toLowerCase()]}</label>`);
    const no = $(`<label data-answer="false" class="q-label" tabindex="${getTabIndex()}"><input name="slide-${slide}-quiz" type="radio" value="false"><span></span>${translations[Q3.cooboLang][boolChoices[1].toLowerCase()]}</label>`);
    quizCnt.append($(`<div></div>`).append(yes, no));

    if (!slideQuiz.isSubmitted) {
        $('input[name="slide-' + slide + '-quiz"]').on("change", function (e) {
            $(this).parent().addClass('selected').siblings().removeClass("selected");
            submitBtn.removeClass("disabled");
            Analytics.add({
                type: "quiz-action",
                learningObjectId: quizId,
                eventName: "select-answer",
                answer: e.target.value,
            });
        });
    }
}

function initTextArea({ slideQuiz, quizCnt, slide, submitBtn }) {
    submitBtn.addClass("disabled");
    let input = $(`<textarea  name="slide-${slide}-quiz" ></textarea>`);
    let inputCnt = $('<div class="answer-cnt"></div>');
    let result = $('<span class="result"></span>');
    inputCnt.append(input, result);
    quizCnt.append(inputCnt);

    if (!Utils.isMobile.any()) {
        setTimeout(function () {
            input[0].focus();
        }, 100);
    }
    if (slideQuiz.isSubmitted) {
        input.html(slideQuiz.userAnswer);
        input.attr("disabled", "");
        input.addClass(slideQuiz.isSubmitted);
    } else {
        input.on('keyup', function (event) {
            input.html(input.val());
            slideQuiz.userAnswer = input.val();
            submitBtn.removeClass("confirm");
            submitBtn[input.val() ? "removeClass" : "addClass"]("disabled");
        });
    }
}

function initRegion({ quizWrap, quizCnt, quizQuestionTitleEl, slideQuizScoreEl, slideQuizScore, slide, slideQuiz, submitCnt }) {
    quizWrap.addClass('find-the-parts');

    if (slideQuiz.hideQuizBox) { quizWrap[0].style.display = 'none'; }

    if (slideQuiz.regions.length === 0) {
        quizCnt.html('');
        clearInterval(QuizManager._quizInterval);
        QuizManager.timer = 0;
        quizWrap.removeClass('active');
        Q3.data.slides[slide].slideQuiz.active = false;
    }
    slideQuiz.userScore = 0;
    slideQuiz.userAnswer = 0;
    quizQuestionTitleEl.hide();

    let questionTitleEl = $("<h3 class='question object'></h3>");

    quizCnt.append(questionTitleEl);

    const visibleLayers = [], regions = slideQuiz.regions.slice(0, 1);
    const slideLayers = SlideManager.currentSlide.layers;
    const layers2d = Q3.data.layers.layers2D;

    const isQuizObj = (obj) => {
        if (!obj) return false;
        return obj instanceof Q3.Picture || obj instanceof Q3.Text || obj instanceof Q3.MathFormula || obj instanceof Q3.CustomizableRegion;
    };

    Object.keys(slideLayers).forEach(uuid => {
        if (!slideLayers[uuid]) return;

        const layer = layers2d[uuid];

        if (!layer) return;

        if (isQuizObj(LayersManager.list[uuid].objects[0]))
            visibleLayers.push(uuid);
    });

    const submitBtn = $(`<button class="quiz-msg-btn submit-btn disabled" data-confirm="${QuizManager.translation.confirm}" data-confirm-mobile="${QuizManager.translation.confirmMobile}">${QuizManager.translation.submit}</button>`);
    submitCnt.append(submitBtn);

    function addRegion(i) {
        const finishQuiz = () => {
            submitBtn.removeClass("disabled");
            submitBtn.removeClass("confirm");
            Analytics.add({
                type: "quiz-submit",
                text: `Submitted slide${SlideManager.activeSlide + 1} quiz.`
            });
            QuizManager.submitQuiz({ submitBtn })
        }

        const region = regions[i];

        if (!region) return;

        let correctAnswers = [region.uuid];
        if (region.additional && region.additional.length > 0) {
            correctAnswers = correctAnswers.concat(region.additional);
        }
        const regionLayers = LayersManager.stack.filter(l => l.objects[0]);
        correctAnswers = correctAnswers.filter(uuid => regionLayers.find(l => l.objects[0].uuid === uuid));
        let givenAnswers = [];

        let regionName = correctAnswers.map(answer => Object.values(Q3.data.layers.layers2D).find(l => Object.keys(l.objects)[0] === answer).name).join(", ");
        let defQ = [translations[Q3.cooboLang].find, regionName];
        const question = region.question ? region.question : (Q3.cooboLang === "hindi" ? defQ.reverse().join(" ") : defQ.join(" "));

        questionTitleEl.html(question);

        let score = Math.round(slideQuiz.score / regions.length);

        if (i === regions.length - 1) {
            score = slideQuiz.score - Math.round(slideQuiz.score / regions.length) * i;
            QuizManager.answers++;
        }

        const questionEl = $(`<div class="question find-part">${regionName}<span class="score">${score}</span></div>`);

        Q3.selectingMesh = 'quiz';
        function onMouseOver(object) {
            object.clickElement.classList.add("mouseover-2d");
        }
        function onMouseOut(object) {
            object.clickElement.classList.remove("mouseover-2d");
        }

        let clickObjectMap = visibleLayers.filter(uuid => !LayersManager.list[uuid].objects[0].disableEvents).map(uuid => [LayersManager.list[uuid].objects[0],
        handleClick.bind(this, LayersManager.list[uuid].objects[0]),
        onMouseOver.bind(this, LayersManager.list[uuid].objects[0]),
        onMouseOut.bind(this, LayersManager.list[uuid].objects[0])]);

        for (let [object, clickThisObject, onMouseOverThisObject, onMouseOutThisObject] of clickObjectMap) {
            object.clickElement.style.pointerEvents = "auto";
            object.clickElement.addEventListener('click', clickThisObject);
            object.clickElement.addEventListener('mouseover', onMouseOverThisObject);
            object.clickElement.addEventListener('mouseout', onMouseOutThisObject);
        }

        const removeListeners = () => {
            for (let [object, clickThisObject, onMouseOverThisObject, onMouseOutThisObject] of clickObjectMap) {
                object.clickElement.removeEventListener('click', clickThisObject);
                object.clickElement.removeEventListener('mouseover', onMouseOverThisObject);
                object.clickElement.removeEventListener('mouseout', onMouseOutThisObject);
            }
        }

        const onSlideChange = (a, b, c) => {
            if (SlideManager.activeSlide === slide) return;

            // forEveryRegion(object => object.domElement.removeEventListener("click", handleClick));

            Q3.selectingMesh = false;

            Events.unlisten("slidechange", onSlideChange);
        };

        Events.listen("slidechange", onSlideChange);
        const submitClickHandler = () => {
            if (submitBtn.hasClass("disabled") && !submitBtn.hasClass("confirm")) {
                submitBtn.addClass("confirm")
                return;
            }
            submitBtn.removeClass("confirm");
            let passed = true;
            region.givenAnswers = givenAnswers.map(o => o.uuid);
            if (givenAnswers.length !== correctAnswers.length) {
                passed = false;
            }

            givenAnswers.forEach(item => {
                if (correctAnswers.indexOf(item.uuid) === -1) {
                    passed = false;
                    item.clickElement.classList.add("selected-2d-wrong")
                } else {
                    item.clickElement.classList.add("selected-2d-correct")
                }
            });

            if (passed) pass();
            else fail();
            //quizCnt.append(questionEl);
            submitBtn.off("click", submitClickHandler)
        }
        submitBtn.on("click", submitClickHandler);
        function handleClick(object, e) {
            const layer = LayersManager.list[object.parentLayer.uuid];
            region.userAnswer = layer.name;

            if (givenAnswers.indexOf(object) === -1) {
                givenAnswers.push(object);
                object.clickElement.classList.add("selected-2d");
            }
            else {
                givenAnswers.splice(givenAnswers.indexOf(object), 1);
                object.clickElement.classList.remove("selected-2d");
            }

            clearInterval(QuizManager._quizInterval);

            QuizManager.timer = 0;
            slideQuiz.userAnswer = slideQuiz.userScore;

            const showSubmitBtn = show => {
                submitBtn.removeClass("confirm");
                submitBtn[show ? "removeClass" : "addClass"]("disabled");
            }

            const className = slideQuiz.userScore > 0 ? "complete" : "fail";

            slideQuizScoreEl.addClass(className);

            if (className === "complete") { slideQuizScore.html(slideQuiz.userScore); }

            Q3.selectingMesh = false;

            showSubmitBtn(givenAnswers.length > 0);

        }

        function pass() {
            slideQuiz.userScore += score;
            QuizManager.score += score;
            region.score = score;


            questionEl && questionEl.addClass('complete');
            //quizWrap.find(".quiz-cnt").addClass('complete');
            removeListeners();

            if (i < regions.length - 1) {
                addRegion(i + 1);
            }
            else {
                finishQuiz();
            }

            // setTimeout(() => {
            //     // document.querySelectorAll(".selected-2d")
            //     //     .forEach(el => el.classList.remove("selected-2d", "selected-2d-correct", "selected-2d-wrong"));

            //     //quizWrap.find(".quiz-cnt").removeClass('complete');
            //     if (i < regions.length - 1) {
            //         addRegion(i + 1);
            //     }
            //     else {
            //         finishQuiz();
            //     }
            // }, 1500);
        }

        function fail() {
            region.score = 0;

            questionEl && questionEl.addClass('fail');
            // quizWrap.find(".quiz-cnt").addClass('fail');
            removeListeners();

            if (i < regions.length - 1) {
                addRegion(i + 1);
            }
            else {
                finishQuiz();
            }

            // setTimeout(() => {
            //     // document.querySelectorAll(".selected-2d")
            //     //     .forEach(el => el.classList.remove("selected-2d", "selected-2d-correct", "selected-2d-wrong"));
            //     // quizWrap.find(".quiz-cnt").removeClass('fail');
            //     if (i < regions.length - 1) {
            //         addRegion(i + 1);
            //     }
            //     else {
            //         finishQuiz();
            //     }
            // }, 1500);
        }
    }

    addRegion(0);
}

function initSelect({ quizWrap, quizCnt, quizQuestionTitleEl, slideQuizScoreEl, slideQuizScore, slide, slideQuiz, submitCnt }) {
    quizWrap.addClass('find-the-parts');

    if (slideQuiz.hideQuizBox) { quizWrap[0].style.display = 'none'; }
    if (slideQuiz.meshes.length === 0) {
        quizCnt.html('');
        clearInterval(QuizManager._quizInterval);
        QuizManager.timer = 0;
        quizWrap.removeClass('active');
        Q3.data.slides[slide].slideQuiz.active = false;
    }
    let submitBtn = $(`<button class="quiz-msg-btn submit-btn disabled" data-confirm="${QuizManager.translation.confirm}" data-confirm-mobile="${QuizManager.translation.confirmMobile}">${QuizManager.translation.submit}</button>`);
    submitCnt.append(submitBtn);
    // slideQuiz.userScore = 0;

    let i = 0;

    quizQuestionTitleEl.hide();

    let questionTitleEl = $("<h3 class='question object'></h3>");

    quizCnt.append(questionTitleEl);
    let qScore = Math.round(slideQuiz.score / slideQuiz.meshes.length);

    // slideQuiz.meshes.forEach((m, ind) => {
    //     if (m.userAnswer) {
    //         let questionEl = $('<div class="question find-part">'
    //             + slideQuiz.meshes[ind].name
    //             + '<span class="score">'
    //             + qScore + '</span></div>');
    //         questionEl.addClass(m.score ? "complete" : "fail");
    //         quizCnt.append(questionEl);
    //         i++;
    //     }
    // });

    submitBtn.addClass("disabled");
    let meshes = [];
    RenderManager.activeScene.traverse((node) => {
        if (node instanceof THREE.Mesh && node.visible) {
            meshes.push(node);
        }
    });

    let index = 0;
    let selectedMesh;

    function selectMeshByKey(e) {
        if (e.key === "Tab") {
            e.preventDefault();
            RenderManager.setHighlightObjects([meshes[index]]);
            RenderManager.activeCamera.update = true;
            selectedMesh = meshes[index];
            index++;
            if (index > meshes.length - 1) {
                index = 0;
            }
        }
    }
    window.addEventListener("keydown", selectMeshByKey);

    function pickPart(i) {
        let quizMesh = slideQuiz.meshes[i]
        let isMultiple = quizMesh && quizMesh.additional && quizMesh.additional.length;
        const { selectRootsOnly } = slideQuiz;
        let quizSelectedMeshes = [];
        quizCnt.removeClass('fail');
        quizCnt.removeClass('complete');
        if (!slideQuiz.meshes[i]) return;
        let question = slideQuiz.meshes[i].question
            ? slideQuiz.meshes[i].question
            : "Find " + slideQuiz.meshes[i].name;
        const submitMesh = () => {
            if (submitBtn && submitBtn.hasClass("disabled") && !submitBtn.hasClass("confirm")) {
                submitBtn.addClass("confirm")
                return;
            }
            submitBtn.off("click", submitMesh);
            quizMesh.userAnswer = quizSelectedMeshes.map(m => m.uuid);
            Analytics.add({
                type: "quiz-action",
                learningObjectId: quizId,
                eventName: "select-answer",
                answer: quizSelectedMeshes.map(obj => {
                    const meshLabel = Q3.labels.live._meshLabels[obj.id];
                    const content = meshLabel ? meshLabel._labels[0]._element.content : false;
                    return (meshLabel && content) ? content : obj.name;
                }).join(", "),
            });
            const correct = [quizMesh.uuid[0], ...(quizMesh.additional || []).map(a => a.uuid[0])];
            const answersCountCond = slideQuiz.answersCountCond ? quizMesh.userAnswer.length === correct.length : true;
            if (answersCountCond && quizMesh.userAnswer.length && quizMesh.userAnswer.every(uuid => correct.indexOf(uuid) !== -1)) pass();
            else fail();
            $(Q3.container).off("mousedown", onMouseDown).off("mouseup", onMouseUp);

            Q3.selectingMesh = false;
            Q3.selectingMeshRootOnly = false;
            if (slideQuiz.fuzzySelection) {
                setFuzzyObjectSelection(false);
            }
            questionEl && quizCnt.append(questionEl);
            let materialsData = Q3.data.assets.materials;
            let slideAnim = SlideManager.currentSlide.animation;
            quizSelectedMeshes.forEach(obj => {
                let objColor = slideAnim[obj.uuid].color;
                let materials = ensureArray(obj.material);
                for (let material of materials) {
                    let matData = materialsData[material.uuid];
                    let matAnim = slideAnim[material.uuid];
                    let matColor = matAnim && matAnim.color || objColor || matData && matData.color || 0xffffff;
                    material.color.setHex(matColor);
                }
            });
            quizSelectedMeshes = [];
            RenderManager.setPreselectObjects(quizSelectedMeshes, "passOutlineQuiz");
            RenderManager.activeCamera.update = true;
            RenderManager.setHighlightObjects([]);
            RenderManager.activeCamera.update = true;


            if (i === slideQuiz.meshes.length - 1) {
                clearInterval(QuizManager._quizInterval);

                QuizManager.timer = 0;

                let className = slideQuiz.userScore > 0 ? "complete" : "fail";

                slideQuizScoreEl.addClass(className);
                window.removeEventListener("keydown", selectMeshByKey);
                RenderManager.setHighlightObjects([]);
                RenderManager.activeCamera.update = true;

                if (className === "complete") { slideQuizScore.html(slideQuiz.userScore); }

                QuizManager.submitQuiz({ submitBtn })

                if (slideQuiz.isRetakable) {
                    slideQuiz.meshes.forEach((m) => {
                        delete m.score;
                        delete m.userAnswer;
                    });
                    slideQuiz.userAnswer = false;
                    slideQuiz.userScore = 0;
                }

                // Analytics.add({
                //     type: "quiz-submit",
                //     text: `Submitted slide${SlideManager.activeSlide + 1} quiz.`
                // });
            }


            function pass() {
                slideQuiz.userScore += score;
                QuizManager.score += score;
                slideQuiz.meshes[i].score = score;

                questionEl && questionEl.addClass('complete');
                //quizCnt.addClass('complete');

                setTimeout(() => pickPart(i + 1), 1500);
            }

            function fail() {
                slideQuiz.meshes[i].score = 0;

                questionEl && questionEl.addClass('fail');
                //quizCnt.addClass('fail');

                setTimeout(() => {
                    pickPart(i + 1);
                }, 1500);
            }
        };
        submitBtn.on("click", submitMesh);
        questionTitleEl.html(question);

        let score = Math.round(slideQuiz.score / slideQuiz.meshes.length);

        if (i === slideQuiz.meshes.length - 1) {
            score = slideQuiz.score - Math.round(slideQuiz.score / slideQuiz.meshes.length) * (i);
            QuizManager.answers++;
        }

        let questionEl = null;
        // let questionEl = $('<div class="question find-part">'
        //     + slideQuiz.meshes[i].name
        //     + '<span class="score">'
        //     + score + '</span></div>');

        Q3.selectingMesh = true;
        Q3.selectingMeshRootOnly = slideQuiz.selectRootsOnly;
        if (slideQuiz.fuzzySelection) {
            setFuzzyObjectSelection(true);
        }

        let isDragging = false;
        let moved = 0;

        $(Q3.container).on("mousedown", onMouseDown).on("mouseup", onMouseUp);
        $(Q3.container).on("touchstart", onMouseDown).on("touchend", onMouseUp);

        function onMouseDown(e) { $(window).on("mousemove", onMouseMove); $(window).on("touchmove", onMouseMove); }

        function onMouseMove(e) {
            moved++;
            if (moved >= 5) {
                isDragging = true;
                $(window).off("mousemove", onMouseMove);
                $(window).off("touchmove", onMouseMove);
            }
        }

        function onMouseUp(e) {
            let wasDragging = isDragging;

            isDragging = false;
            moved = 0;

            $(window).off("mousemove", onMouseMove);
            $(window).off("touchmove", onMouseMove);
            if (!wasDragging) { handleClick(e, {selectRootsOnly}); }
        }

        function onEnterPress(e) {
            if (e.key === "Enter" && selectedMesh) {
                e.preventDefault();
                e.stopImmediatePropagation();
                handleClick(selectedMesh, {selectRootsOnly});
                window.removeEventListener("keydown", onEnterPress);
            }
        }

        window.addEventListener("keydown", onEnterPress);

        const onSlideChange = () => {
            if (SlideManager.activeSlide === slide) return;

            $(Q3.container).off("mousedown", onMouseDown).off("mouseup", onMouseUp);
            $(Q3.container).off("touchstart", onMouseDown).off("touchend", onMouseUp);

            Q3.selectingMesh = false;
            Q3.selectingMeshRootOnly = false;
            if (slideQuiz.fuzzySelection) {
                setFuzzyObjectSelection(false);
            }
            RenderManager.setPreselectObjects([], "passOutlineQuiz");
            Events.unlisten("slidechange", onSlideChange);
            window.removeEventListener("keydown", selectMeshByKey);
        };

        Events.listen("slidechange", onSlideChange);

        function handleClick(e, {selectRootsOnly}) {
            let meshes;

            if (e instanceof THREE.Mesh) { meshes = [e]; }
            else {
                if (e.target !== RenderManager.overlay2d) return;
                meshes = Q3.getMeshUnderMouse(e.changedTouches ? e.changedTouches[0] : e);
                if (!meshes.length || meshes.length === 1 && meshes[0] === RenderManager.background) {
                    return;
                }
            }
            const animData = SlideManager.currentSlide.animation;
            let obj = meshes[0];
            if (animData[obj.uuid] && animData[obj.uuid].disableInteractivity) {
                return;
            }
            const objs = [];

            if (selectRootsOnly) {
                const getRoot = (object) => {
                    if (Q3.TransformModifiers.isTopLevelOrdinaryObject(object)) {
                        return object;
                    } else {
                        const parent = Q3.TransformModifiers.getOrdinaryParent(object);
                        return getRoot(parent);
                    }
                };

                const root = getRoot(obj);

                root.traverse(o => {
                    if (o.isMesh || o.isSkinnedMesh) {
                        objs.push(o);
                    }
                });
            } else {
                objs.push(obj);
            }

            for (const obj of objs) {
                if (isMultiple) {
                    let ind = quizSelectedMeshes.indexOf(obj);
                    if (ind === -1) {
                        quizSelectedMeshes.push(obj)
                    } else {
                        quizSelectedMeshes.splice(ind, 1);
                    }
                } else {
                    if (quizSelectedMeshes[0] === obj) {
                        quizSelectedMeshes = [];
                    } else {
                        quizSelectedMeshes = [obj];
                    }
                }
            }

            if (!Q3.editMode) {
                const slideQuiz = SlideManager.currentSlide.slideQuiz;
                slideQuiz.hasSelected = quizSelectedMeshes.length > 0;
            }

            RenderManager.setPreselectObjects(quizSelectedMeshes, "passOutlineQuiz");
            RenderManager.activeCamera.update = true;
            submitBtn.removeClass("confirm");
            submitBtn[quizSelectedMeshes.length ? "removeClass" : "addClass"]("disabled");
        }
    }

    pickPart(i);
}

function initDragLabels({ quizWrap, quizCnt, quizQuestionTitleEl, slide, slideQuiz, submitBtn }) {
    submitBtn.addClass('disabled');
    quizWrap.addClass('find-the-parts');
    if (!slideQuiz.meshes.length || !Object.keys(slideQuiz.labels).length) {
        quizCnt.html('');
        clearInterval(QuizManager._quizInterval);
        QuizManager.timer = 0;
        quizWrap.removeClass('active');
    }

    $(".livelabel").addClass('hidden-by-quiz');

    let answersCnt = $('<div class="answers-cnt"></div>');

    quizCnt.append(answersCnt);

    const meshlabels = Object
        .keys(slideQuiz.labels)
        .map(labelUuid => Q3.labels.live.getLabelByUuid(labelUuid));


    let meshLabelsObj = {};

    function onDrop(l, e, draggable) {
        if (slideQuiz.labels[$(draggable.draggable).data("uuid")].userAnswer) return;
        submitBtn.removeClass('disabled');
        slideQuiz.labels[$(draggable.draggable).data("uuid")].userAnswer = meshLabelsObj[l.uuid].content;
        slideQuiz.labels[l.uuid].trueLabel = slideQuiz.labels[$(draggable.draggable).data("uuid")].name;
        $(e.target).droppable("disable");

        let resetBtn = $('<div title="Reset" class="quiz-msg-btn reset-btn"><img src="/images/icons/cancel.svg" alt=""></div>');

        resetBtn.on("click touchstart", () => {
            $(e.target).droppable("enable");
            answersCnt.append(draggable.draggable);
            $(draggable.draggable)[0].style.removeProperty("left");
            $(draggable.draggable)[0].style.removeProperty("top");
            $(e.target).html("Drop answer here");
            delete slideQuiz.labels[$(draggable.draggable).data("uuid")].userAnswer;
        });

        $(draggable.draggable)[0].style.removeProperty("left");
        $(draggable.draggable)[0].style.removeProperty("top");

        $(e.target).html("").append(draggable.draggable).append(resetBtn);
    }

    for (let i = 0; i < meshlabels.length; i++) {
        let label = meshlabels[i];
        const el = label._element;
        const uuid = el.uuid;
        meshLabelsObj[uuid] = el;

        if (slideQuiz.labels[uuid]) {
            if (slideQuiz.labels[uuid].enabled) {
                $(el.domElement)
                    .find(".drop-here")
                    .remove(".drop-here");
                $(el.domElement)
                    .removeClass('hidden-by-quiz')
                    .addClass('answer-placeholder');

                const droppable = $('<div class="drop-here">Drop answer here</div>');
                droppable.droppable({
                    accept: ".answer.ui-draggable",
                    classes: { "ui-droppable-active": "ui-droppable-active" },
                    drop: onDrop.bind(null, el),
                    over: () => el._onMouseOver(),
                    out: () => el._onMouseLeave()
                });

                $(el.domElement).append(droppable);


                if (!slideQuiz.isSubmitted) {
                    const draggableAnswer = $('<div class="answer">' + el.content + '</div>');

                    answersCnt.append(draggableAnswer);

                    draggableAnswer.draggable({
                        containment: "#viewer",
                        scroll: false,
                        revert: "invalid"
                    });

                    draggableAnswer.attr("data-uuid", uuid);

                } else {
                    const telement = label.element.domElement.querySelector(".drop-here.ui-droppable");
                    const userAnswer = slideQuiz.labels[uuid].trueLabel;
                    const isCorrect = slideQuiz.labels[uuid].name === userAnswer;

                    label.element.domElement.classList.add(isCorrect ? "correct" : "wrong")
                    if (userAnswer) {
                        telement.classList.add("ui-droppable-disabled");
                        telement.innerText = userAnswer;
                    }
                }
            } else {
                delete slideQuiz.labels[uuid];
            }
        }
    }

    if (slideQuiz.meshes.length) {
        const question = slideQuiz.question;
        quizQuestionTitleEl.html(question);

        MeshIsolation.show(slideQuiz.meshes.map(({ uuid }) => uuid).flat());
    }

    Q3.labels.live._renderLabels();

    if (slideQuiz.isSubmitted) replaceWithRevealButton(slideQuiz, submitBtn);

    // quizWrap.draggable({
    //     containment: "#viewer",
    //     handle: "h2.type-heading, h3.question, .question-heading, .question-container",
    //     scroll: false
    // });
}

function initIsolated({ quizWrap, quizCnt, quizQuestionTitleEl, slide, slideQuiz, submitBtn }) {
    quizWrap.addClass('find-the-parts');
    submitBtn.addClass('disabled');

    if (!slideQuiz.meshes.length || !slideQuiz.meshes[0].rightAnswer) {
        quizCnt.html('');

        clearInterval(QuizManager._quizInterval);

        quizWrap.removeClass('active');

        Q3.data.slides[slide].slideQuiz.active = false;
        return;
    }

    $('.overlay2d').addClass('hide-labels');

    const mesh = slideQuiz.meshes[0];
    if (mesh) {
        quizQuestionTitleEl.html(mesh.question);
        slideQuiz.rightAnswer = mesh.rightAnswer.length
            ? mesh.rightAnswer
            : mesh.name;
    }

    const input = $(`<input type="text" autocomplete="off" name="slide-${slide}-quiz" >`);

    quizCnt.append(input);

    if (mesh) {
        MeshIsolation.show(slideQuiz.meshes[0].uuid);
    }

    if (!Utils.isMobile.any()) {
        setTimeout(function () {
            input[0].focus();
        }, 100);
    }

    input.on('keyup', function (e) {
        submitBtn.removeClass("confirm");
        submitBtn[e.target.value.length ? "removeClass" : "addClass"]('disabled');
        if (event.keyCode === 13) {
            QuizManager.submitQuiz({ submitBtn });
        }
        input.attr("value", input.val())
    });
}

let dragSortable;

function dragByKey(e) {
    if ((e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || !document.activeElement.classList.contains("draggable-li")) {
        return;
    }
    if (e.key === 'ArrowDown') {
        $(document.activeElement).next().insertBefore($(document.activeElement))
    }
    if (e.key === 'ArrowUp') {
        $(document.activeElement).prev().insertAfter($(document.activeElement))
    }

}

function initDrag({ quizCnt, slideQuiz, quizQuestionTitleEl }) {
    const dragIcon = `<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.7712 0.00588185L18.7674 0.00962865L18.7578 0L16.8905 1.86732L16.9001 1.87693L12.8909 5.8861C12.3758 6.40127 12.3657 7.24024 12.8849 7.75941C13.4071 8.2816 14.2436 8.26799 14.7582 7.75338L17.4456 5.06598V23.8456C17.4456 24.5735 18.0309 25.1684 18.766 25.1684C19.4996 25.1684 20.0863 24.5641 20.0863 23.8456V5.06306L22.7708 7.74751C23.2854 8.26212 24.1219 8.27573 24.6441 7.75354C25.1632 7.23437 25.1532 6.3954 24.638 5.88023L20.6346 1.87681L20.6384 1.87307L18.7712 0.00588185Z" fill="#5073E4"></path>
        <path d="M8.63841 23.4637L8.63467 23.46L12.638 19.4566C13.1532 18.9415 13.1632 18.1025 12.6441 17.5833C12.1219 17.0611 11.2854 17.0747 10.7708 17.5893L8.17898 20.1811V1.49127C8.17898 0.77277 7.59226 0.168429 6.85862 0.168429C6.12357 0.168429 5.53825 0.763366 5.53825 1.49127V20.3635L2.7582 17.5835C2.24358 17.0689 1.40707 17.0553 0.884882 17.5774C0.365711 18.0966 0.375751 18.9356 0.890919 19.4508L4.90017 23.46L4.89056 23.4696L6.75784 25.3369L6.76745 25.3273L6.77116 25.331L8.63841 23.4637Z" fill="#5073E4"></path>
    </svg>`
    let rndArr = shuffleArray(slideQuiz.answers);

    // slideQuiz.userAnswer = JSON.stringify(rndArr);
    slideQuiz.rightAnswer = JSON.stringify(slideQuiz.answers);

    let dragableCnt = $('<ul class="draggable-cnt"></ul>');

    for (let i = 0; i < rndArr.length; i++) {
        let answer = $(`<li data-answer="${rndArr[i]}" tabindex="100" class="draggable-li q-label">
               <span class="dragger">${dragIcon}</span><span class="result"></span>${rndArr[i]}</li>`);
        dragableCnt.append(answer);
    }
    let scroll = $(`<div class="quiz-scroll pointer-auto"></div>`);
    quizCnt.append(scroll);
    scroll.append(dragableCnt);
    dragSortable = Sortable.create(dragableCnt[0], {
        forceFallback: true,
        fallbackOnBody: true,

        onStart: function () {
            dragableCnt.addClass('is-dragging');
        },
        onEnd: function () {
            dragableCnt.removeClass('is-dragging');
        }
    });
    window.addEventListener('keydown', dragByKey);
    setTimeout(() => {
        if (slideQuiz.layout !== "twoColumns") {
            setFontSize(quizQuestionTitleEl, scroll)
        }
    }, 100)

}

function initConnect({ quizWrap, quizCnt, slideQuiz, submitBtn }) {
    submitBtn.addClass("disabled");
    if (!slideQuiz.resultConnections) {
        slideQuiz.resultConnections = slideQuiz.connections.slice(0);
    }
    slideQuiz.connections = [];

    let connectQuizCnt = $("<div class='connect-quiz viewer-mode'></div>");
    let connectQuiz = $("<div class='graphContainer'></div>");
    quizCnt.append(connectQuizCnt);
    quizWrap.addClass('find-the-parts');

    connectQuizCnt.append(connectQuiz)
    resize();

    Graph.init(connectQuiz[0], slideQuiz, { connections: slideQuiz.resultConnections }, true, () => { submitBtn.removeClass("disabled") })
}

function initMatrix({ quizQuestionTitleEl, scroll, quizCnt, slide, slideQuiz, submitBtn }) {
    submitBtn.addClass("disabled");
    if (!slideQuiz.isSubmitted) {
        slideQuiz.userAnswer = {};
    }
    const matrixQuiz = $(`<div class="matrix-quiz"></div>`);
    quizCnt.append(matrixQuiz);
    const table = $(`<table></table>`);
    matrixQuiz.append(table)
    const head = $(`<tr><th></th></tr>`);
    table.append(head);
    const c = slideQuiz.matrixCols;
    const r = slideQuiz.matrixRows;
    const colHeadingStr = 'pqrstuvwxy';
    const rowHeadingStr = 'abcdefghij';
    const colTh = colHeadingStr.slice(0, c).split("");
    const rowTh = rowHeadingStr.slice(0, r).split("");
    colTh.forEach((cTh) => {
        head.append(`<th>${cTh}</th>`)
    });
    rowTh.forEach((rTh, rI) => {
        let tr = $(`<tr><th>${rTh}</th></tr>`);
        table.append(tr);
        colTh.forEach((cTh, cI) => {
            let td = $(`<td></td>`);
            tr.append(td);
            let answer = cI + " " + rI;
            if (!slideQuiz.isSubmitted) {
                td.on("click", () => {
                    if (slideQuiz.userAnswer[answer]) {
                        delete slideQuiz.userAnswer[answer];
                        td.removeClass("selected")
                    } else {
                        slideQuiz.userAnswer[answer] = true;
                        td.addClass("selected");
                    }
                    submitBtn.removeClass("confirm");
                    submitBtn[slideQuiz.userAnswer && Object.keys(slideQuiz.userAnswer).length ? "removeClass" : "addClass"]("disabled");
                });
            }
        })
    })
    setTimeout(() => {
        setFontSize(quizQuestionTitleEl, scroll)
    }, 100)
}

function initRadio({ quizCnt, slide, slideQuiz, submitBtn, quizQuestionTitleEl }) {
    submitBtn.addClass("disabled");

    let radioQuiz = slideQuiz.radioQuiz;

    let answerOrder = radioQuiz.answers.map((answer, i) => i);
    if (radioQuiz.randomOrder) {
        answerOrder = shuffleArray(answerOrder);
    }
    let i = 0;
    let scroll = quizCnt;
    let answers = $(`<div class="answers"></div>`)
    for (let answerIndex of answerOrder) {
        let answer = radioQuiz.answers[answerIndex];
        let inputType = radioQuiz.multipleCorrect ? 'checkbox' : 'radio';

        let answerDom = $(`<label class="q-label" tabindex="100"><input name="slide-${slide}-quiz" type="${inputType}" value="${answerIndex}"><span class="let">${String.fromCharCode(i + "a".charCodeAt(0)).toUpperCase()}</span><span class="answer-text">${answer.text}</span></label>`);
        answer.$el = answerDom;
        answers.append(answerDom);
        i++
    }
    scroll.append(answers);

    if (!slideQuiz.isSubmitted) {
        if (radioQuiz.multipleCorrect) {
            $('input[name="slide-' + slide + '-quiz"]').on("change", function (e) {
                $(this).parent().toggleClass("selected");
                Analytics.add({
                    type: "quiz-action",
                    learningObjectId: quizId,
                    eventName: "select-answer",
                    answer: radioQuiz.answers[e.target.value],
                });
                submitBtn.removeClass("confirm");
                submitBtn[answers.find(".selected").length ? "removeClass" : "addClass"]("disabled");
            });
        } else {
            $('input[name="slide-' + slide + '-quiz"]').on("change", function (e) {
                $(this).parent().addClass('selected').siblings().removeClass("selected");
                Analytics.add({
                    type: "quiz-action",
                    learningObjectId: quizId,
                    eventName: "select-answer",
                    answer: radioQuiz.answers[e.target.value],
                });
                submitBtn.removeClass("confirm");
                submitBtn[answers.find(".selected").length ? "removeClass" : "addClass"]("disabled");
            });
        }
    }
    setTimeout(() => {
        if (isScroll(answers) && slideQuiz.layout !== "twoColumns") setFontSize(quizQuestionTitleEl, answers);
    }, 100)

}

function initDragDrop3D({ quizCnt, slideQuiz, submitBtn }) {
    AssessmentManager.clearAssessments();
    AssessmentManager.inAssessmentMode = true;

    const objects = AssetLibrary.objects;
    const animData = SlideManager.currentSlide.animation
    const meshes = slideQuiz.meshes.filter(obj => !(animData[obj.uuid] && animData[obj.uuid].disableInteractivity));

    function onPickup() {
        const slideQuiz = SlideManager.currentSlide.slideQuiz;
        if (!(slideQuiz && slideQuiz.active && slideQuiz.type === "plopQuiz")) return;

        if (!AssessmentManager.assessments.some(ass => ass.isPlopped)) {
            if (QuizManager.useSnapping) submitBtn.addClass("disabled");
        }
    }

    const assessments = [];

    for (let mesh of meshes) {
        const object = objects[mesh.uuid];

        if (object) {
            const ass = AssessmentManager.createAssessment("mesh-plop", {
                object: objects[mesh.uuid],
                matrix: new THREE.Matrix4().fromArray(mesh.matrix),
                sizeSm: {
                    matrix: new THREE.Matrix4().fromArray(mesh.sizeSm ? mesh.sizeSm : mesh.matrix)
                },
                onPlop: () => {
                    submitBtn.removeClass("disabled");
                    Analytics.add({
                        type: "quiz-action",
                        learningObjectId: quizId,
                        eventName: "select-answer",
                        answer: getMeshDisplayName(mesh.uuid)
                    });
                },
                onPickup
            });

            ass.release();

            assessments.push(ass);

            if ("solved" in mesh && "score" in mesh) {
                ass.setSolved(mesh.score > 0);
                mesh.finalMatrix.decompose(ass.object.position, ass.object.quaternion, ass.object.scale);
            }
        } else {
            console.warn('Missing object in Drag and Drop (3D) quiz: ' + mesh.uuid);
        }
    }

    if (QuizManager.useSnapping) submitBtn.addClass("disabled");
    if (slideQuiz.isSubmitted) replaceWithRevealButton(slideQuiz, submitBtn);

    slideQuiz.assessments = assessments;
}

function replaceWithRevealButton(slideQuiz, submitBtn) {
    const submitCnt = submitBtn.parent();
    const animData = SlideManager.currentSlide.animation
    const meshes = slideQuiz.meshes.filter(obj => !(animData[obj.uuid] && animData[obj.uuid].disableInteractivity));

    submitCnt.children().hide();

    if (slideQuiz.isSubmitted && slideQuiz.score !== parseFloat(slideQuiz.userScore)) {
        const btn = $("<div class='quiz-msg-btn submit-btn continue'>Reveal Answer</div>");
        let showingAnswer = false;

        submitCnt.append(btn);

        submitCnt.on("click", () => {
            if (!showingAnswer) {
                switch (slideQuiz.type) {
                    case "plopQuiz":
                        AssessmentManager.assessments.forEach(ass => {
                            ass.plopObject.matrix.decompose(ass.object.position, ass.object.quaternion, ass.object.scale);
                            ass.setSolved(true);
                        });
                        break;
                    case "draglabels":
                        Object.keys(slideQuiz.labels).forEach(key => {
                            const label = Q3.labels.live.getLabelByUuid(key);

                            ["wrong"].forEach(classname => {
                                if (label.element.domElement.classList.contains(classname))
                                    label.element.domElement.classList.remove(classname);
                            });

                            label.element.domElement.classList.add("correct");

                            const telement = label.element.domElement.querySelector(".drop-here.ui-droppable");

                            telement.classList.add("ui-droppable-disabled");
                            telement.innerText = slideQuiz.labels[key].name;
                        });
                        break;
                }

                btn.text("Show answer");
            } else {
                switch (slideQuiz.type) {
                    case "plopQuiz":
                        AssessmentManager.assessments.forEach((ass, i) => {
                            const mesh = meshes[i];
                            mesh.finalMatrix.decompose(ass.object.position, ass.object.quaternion, ass.object.scale);
                            ass.setSolved(mesh.score > 0);
                        });
                        break;
                    case "draglabels":
                        Object.keys(slideQuiz.labels).forEach(key => {
                            const label = Q3.labels.live.getLabelByUuid(key);

                            ["wrong", "correct"].forEach(classname => {
                                if (label.element.domElement.classList.contains(classname))
                                    label.element.domElement.classList.remove(classname);
                            });

                            const actualAnswer = slideQuiz.labels[key].trueLabel;
                            const telement = label.element.domElement.querySelector(".drop-here.ui-droppable");

                            label.element.domElement.classList.add(actualAnswer === slideQuiz.labels[key].name ? "correct" : "wrong");
                            if (!actualAnswer) telement.classList.remove("ui-droppable-disabled");
                            telement.innerText = actualAnswer || "Drop answer here";
                        });
                        break;
                }

                btn.text("Reveal answer");
            }


            showingAnswer = !showingAnswer;
        });
    }
}

function initFraction({ quizCnt, slideQuiz }) {
    let fractionCnt = $("<div class='flex-center'></div>");

    fractionCnt.append(createFractionDom(slideQuiz.numerator, slideQuiz.denominator));

    let { assessments, objects } = createPieAssessment(Math.min(Math.ceil(slideQuiz.numerator / slideQuiz.denominator), 3));

    slideQuiz.assessments = assessments;
    slideQuiz.objects = objects;
    slideQuiz.rightAnswer = String(slideQuiz.numerator * (12 / slideQuiz.denominator));

    AssessmentManager.inAssessmentMode = true;

    let resetBtn = $("<div class='clear-btn quiz-msg-btn' title='Clear Input'>↺</div>");

    resetBtn.click(() => {
        assessments.forEach((a) => {
            a.cleanup();
        });
    });

    fractionCnt.append(resetBtn);
    quizCnt.append(fractionCnt);
}

let carQuizState = null;

function initInteractiveCar({ slideQuiz, slide }) {
    let car = AssetLibrary.objects[slideQuiz.carObject];
    if (!car) return;

    if (!carQuizState) {
        requestAnimationFrame(updateInteractiveCar);
    }

    carQuizState = {
        object: car,
        validConditionStart: null,
        quizData: slideQuiz,
        slide: slide,
    };
    slideQuiz.rightAnswer = "true";
}

let carDirProperty = { free: 'speed', x: 'velocityX', y: 'velocityY', custom: 'velocityUserAxis' };

let carCompare = {
    less: (a, b) => a < b,
    more: (a, b) => a > b,
    lessEq: (a, b) => a <= b,
    moreEq: (a, b) => a >= b,
};

function isScroll(el) {
    return el[0].scrollHeight > el[0].offsetHeight;
}

function setFontSize(titleEl, scrollEl, multiplier = 1) {
    if (!titleEl[0] || !scrollEl[0]) return;
    if (multiplier !== 1) {
        titleEl[0].style.fontSize = `${2*multiplier}rem`;
        return;
    }
    let step = 2; //px
    titleEl[0].style.removeProperty("font-size");
    changeFontSize(parseFont(titleEl[0]));

    function changeFontSize(px) {
        if (px - step > minFontSize()) {
            $(titleEl[0]).css('font-size', `${ptr(px - step)}`);
            if (isScroll(scrollEl)) {
                changeFontSize(parseFont(titleEl[0]));
            };
        }
    }

    function parseFont(el) {
        return parseFloat($(el).css('font-size'))
    }

    function ptr(px) {
        let x = parseFont('html');
        return (1 / x * px) + 'rem';
    };
}

function updateInteractiveCar() {
    if (!carQuizState) return;

    if (carQuizState.slide !== SlideManager.activeSlide) {
        uninitInteractiveCar();
        return;
    }

    let quizData = carQuizState.quizData;
    let { carDirection, carComparator, carVelocity, carVelRatio, carUseRatio, carUseAbsVal, carDuration } = quizData;

    let velocity = carQuizState.object[carDirProperty[carDirection]] * 3.6 * 30;
    let leftCond = velocity;
    if (carUseRatio) {
        let speed = carQuizState.object.speed * 3.6 * 30;
        leftCond /= speed;
    } else {
        leftCond = Math.round(leftCond);
    }
    if (carUseAbsVal) {
        leftCond = Math.abs(leftCond);
    }

    let rightCond = carUseRatio ? carVelRatio : carVelocity;

    let validCondition = carCompare[carComparator](leftCond, rightCond);

    let now = Date.now();
    if (validCondition) {
        if (carQuizState.validConditionStart === null) {
            carQuizState.validConditionStart = now;
        }
    } else {
        carQuizState.validConditionStart = null;
    }

    if (carQuizState.validConditionStart !== null) {
        let quizPassTime = carQuizState.validConditionStart + carDuration * 1000;
        if (now >= quizPassTime) {
            uninitInteractiveCar();
            quizData.userAnswer = "true";
            QuizManager.submitQuiz();
        }
    }

    if (carQuizState) {
        requestAnimationFrame(updateInteractiveCar);
    }
}

function uninitInteractiveCar() {
    carQuizState = null;
}

let dragDrop = null;

function initDragDrop2D(params) {
    $("#app").addClass("quiz-dragdrop2d-addimage");
    let { slideQuiz, submitBtn } = params;
    let { dragDrop2dQuiz } = slideQuiz;
    submitBtn.addClass("disabled");
    submitBtn[0].addEventListener("click", () => {
        $("#app").removeClass("quiz-dragdrop2d-addimage");
    }, { once: true })
    dragDrop = new Q3.DragDrop2DElements((count) => {
        submitBtn.removeClass("confirm");
        submitBtn[count ? "removeClass" : "addClass"]("disabled");
    });
    dragDrop2dQuiz.dragDrop = dragDrop;
    const onSlideLeave = () => {
        // if (!slideQuiz.isSubmitted) dragDrop.resetPositions();
        Q3.Events.unlisten("leaveslide", onSlideLeave);
    };
    Q3.Events.listen("leaveslide", onSlideLeave);

    let objects = {};
    for (let layer of LayersManager.stack) {
        let object = layer.objects[0];
        if (object) {
            objects[object.uuid] = object;
        }
    }
    dragDrop2dQuiz.images = dragDrop2dQuiz.images.filter(i => objects[i.image]);
    let zIndex = 5 + LayersManager.stack.length;
    for (let { image: uuid } of dragDrop2dQuiz.images) {
        let image = objects[uuid];
        image.parentLayer.domElement.style.zIndex = zIndex++;
        if (!image.data.initialPosition) image.data.initialPosition = { x: image.data.sizeLg.x, y: image.data.sizeLg.y }
        image.domElement.style.pointerEvents = "auto"
        dragDrop.addObject(image);
    }

    for (let uuid of dragDrop2dQuiz.regions) {
        let object = objects[uuid];
        if (object instanceof Q3.CustomizableRegion || object instanceof Q3.Picture) {
            dragDrop.addRegion(object);
        }
    }

    if (slideQuiz.isSubmitted) {
        dragDrop.dispose();
    }
}

function getDragDrop2DScore(slideQuiz) {
    let { dragDrop2dQuiz } = slideQuiz;

    let objects = {};
    for (let layer of LayersManager.stack) {
        let object = layer.objects[0];
        if (object) {
            objects[object.uuid] = object;
        }
    }

    let correctCount = 0, inCorrectCount = 0, totalCount = dragDrop2dQuiz.images.reduce((acc, val) => [...acc, ...val.regions.filter(r => acc.indexOf(r) === -1)], []).length;

    for (let { image: imageUuid, regions } of dragDrop2dQuiz.images) {
        let image = objects[imageUuid];
        let region = dragDrop.getObjectRegion(image);

        let correct;

        if (region) {
            correct = regions.indexOf(region.uuid) !== -1;
            let className = correct ? "selected-2d-correct" : "selected-2d-wrong"
            region.highLightElement.classList.add(className)
            region.data.className = className;
            if (!correct) {
                inCorrectCount++;
            }
        }

        if (correct) {
            correctCount++;
        }
    }
    return inCorrectCount ? 0 : Math.round(slideQuiz.score * correctCount / totalCount);
}

function uninitDragDrop2D() {
    if (!dragDrop) return;

    dragDrop.dispose();
    dragDrop = null;
}

function initTimer({ timeEl, slideQuiz }) {
    if (slideQuiz.timeLimitEnabled && slideQuiz.timeLimit > 0) {
        QuizManager.timer = slideQuiz.timeLimit;
        timeEl.html(Utils.secondsToString(QuizManager.timer));
        clearInterval(QuizManager._quizInterval);
        QuizManager._quizInterval = setInterval(function () {
            QuizManager.timer--;
            timeEl.html(Utils.secondsToString(QuizManager.timer));

            if (QuizManager.timer === 0) {
                slideQuiz.userAnswer = "timed out";
                clearInterval(QuizManager._quizInterval);
                QuizManager.submitQuiz({ timeout: true });
            }
        }, 1000);
    } else {
        timeEl.hide();
    }
}

function initFTB({ quizQuestionTitleEl, submitBtn, slideQuiz, quizCnt }) {
    submitBtn.addClass("disabled");
    const ftbQuiz = slideQuiz.fillTheBlanksQuiz;
    let question = ftbQuiz.question;

    let selectedCapsule = null;
    let mousedownCapsule = null;
    let maxW = 50;
    const setSubmitStateCapsule = () => {
        submitBtn.removeClass("confirm");
        submitBtn[ftbQuiz.blanks.filter(b => b.userAnswer).length ? "removeClass" : "addClass"]("disabled");
    }
    const setSubmitStateText = () => {
        submitBtn.removeClass("confirm");
        submitBtn[ftbQuiz.blanks.filter(b => b.$el[0].value).length ? "removeClass" : "addClass"]("disabled");
    }
    const getCenterCoords = (el) => {
        let r = $(el)[0].getBoundingClientRect();
        return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    }
    const revertAnswer = (a) => {
        a.$el.css({ left: 0, top: 0 }).removeClass("dropped")
        a.blank.userAnswer = null;
        a.blank.$el.removeClass("active");
        window.removeEventListener("resize", a.blank.onResize);
        setSubmitStateCapsule()
        delete a.blank;
    }
    const putIntoBlank = (blank, addEvent) => {
        const blPoint = getCenterCoords(blank.$el)
        const cPoint = getCenterCoords(blank.userAnswer.$el);
        const dX = blPoint.x - cPoint.x
        const dY = blPoint.y - cPoint.y
        blank.userAnswer.$el.css({
            left: (parseFloat(blank.userAnswer.$el.css("left")) + dX) + "px",
            top: (parseFloat(blank.userAnswer.$el.css("top")) + dY) + "px",
        })
        if (!slideQuiz.isSubmitted) {
            blank.$el.addClass("active")
        }
        if (addEvent) {
            blank.onResize = (e) => putIntoBlank(blank);
            window.addEventListener("resize", blank.onResize);
        }
        if (slideQuiz.isSubmitted) {
            window.removeEventListener("resize", blank.onResize);
        }
        setSubmitStateCapsule()
    }
    let oneRem = parseFloat($("html").css("font-size"));
    let blankWidth;
    if (ftbQuiz.ftbType === "capsule") {
        let answersEl = $('<div class="answers justify-center"></div>');
        quizCnt.append(answersEl)
        let answers = shuffleArray([...ftbQuiz.answers.filter(a => a.type === "capsule").map((a, i) => ({ index: i, text: a.value })), ...ftbQuiz.wrongAnswers.split(/; */g).filter(a => a).map((a, i) => ({ index: null, text: a }))]);
        answers.forEach((a) => {
            let answerEl = $(`<span class="ftb-capsule">${a.text}</span>`);
            a.$el = answerEl;
            a.$el.draggable({
                containment: "#viewer",
                scroll: true,
                revertDuration: 0,
                stop: () => {
                    if (!a.$el.hasClass("dropped")) {
                        a.$el.css({ left: 0, top: 0 })
                        if (a.blank) {
                            revertAnswer(a);
                        }
                    }
                    setSubmitStateCapsule()
                },
                revert: "invalid"
            });
            answersEl.append(answerEl);
            let w = answerEl[0].offsetWidth;
            if (w > maxW) {
                maxW = w;
            }

            answerEl.on("mousedown", () => {
                mousedownCapsule = a;
                a.$el.removeClass("dropped");
                setSubmitStateCapsule()
            })

            answerEl.on("click touchstart", () => {
                if (a.blank) {
                    revertAnswer(a);
                    selectedCapsule = null;
                    mousedownCapsule = null;
                    return
                }
                if (selectedCapsule === a) {
                    answerEl.removeClass("selected");
                    selectedCapsule = null;
                } else {
                    answerEl.addClass("selected").siblings().removeClass("selected");
                    selectedCapsule = a;
                }
            })
        });
        blankWidth = (maxW + 1 + oneRem) / oneRem + "rem";
        answers.forEach(a => {
            a.$el.css({ width: blankWidth })
        });
        const onResize = () => {
            answers.forEach(a => {
                a.$el.css({ width: "auto" })
            });
            let oneRem = parseFloat($("html").css("font-size"));
            let maxW = Math.max(...answers.map(a => a.$el[0].offsetWidth));
            [...answers, ...ftbQuiz.blanks].forEach(a => {
                a.$el.css({ width: (maxW + 1 + oneRem) / oneRem + "rem" })
            });
        };
        const removeListener = () => {
            window.removeEventListener("resize", onResize);
            Q3.Events.unlisten("slidechange", removeListener)
        };
        Q3.Events.listen("slidechange", removeListener)
        window.addEventListener("resize", onResize);
    }
    ftbQuiz.blanks = slideQuiz.isSubmitted ? ftbQuiz.blanks : [];
    quizQuestionTitleEl.empty().addClass("ftb-question");
    question.forEach((q, i) => {
        quizQuestionTitleEl.append(`<span>${q}</span>`);
        if (i !== question.length - 1) {
            if (slideQuiz.isSubmitted) {
                quizQuestionTitleEl.append(ftbQuiz.blanks[i].$el);
            } else {
                if (ftbQuiz.ftbType === "capsule") {
                    let blank = {
                        $el: $(`<span class="ftb-capsule" style="width: ${blankWidth}"></span>`),
                        userAnswer: null,
                        type: "capsule"
                    };

                    const onClick = () => {
                        let capsule = selectedCapsule || mousedownCapsule;
                        if (capsule) {
                            if (blank.userAnswer) {
                                revertAnswer(blank.userAnswer)
                            }
                            if (capsule.blank && capsule.blank.userAnswer) {
                                revertAnswer(capsule.blank.userAnswer)
                            }
                            blank.userAnswer = capsule;
                            capsule.blank = blank;
                            blank.userAnswer.$el.removeClass("selected").addClass("dropped");
                            putIntoBlank(blank, true);
                            selectedCapsule = null;
                            mousedownCapsule = null;
                        }
                    }

                    blank.$el.droppable({
                        accept: ".ftb-capsule",
                        classes: { "ui-droppable-active": "ui-droppable-active" },
                        drop: onClick
                    });
                    ftbQuiz.blanks.push(blank);
                    quizQuestionTitleEl.append(blank.$el);
                    blank.$el.on("click", onClick);
                } else if (ftbQuiz.ftbType === "input") {
                    let wrap = $(`<span class="ftb-input-wrap"></span>`);
                    let input = $(`<input type="text" class="ftb-capsule" style="width: ${blankWidth}"/>`);
                    let err = $(`<span class="ftb-input-error" style="display: none">Check your Spelling</span>`);

                    let allowedKeys = ["Delete", "Backspace", "ArrowLeft", "ArrowRight", "Home", "End"];
                    input.on('keydown', (e) => {
                        err.fadeOut();
                        let cursorPos = input[0].selectionStart;
                        if (ftbQuiz.answers[i].entryType === "float" || ftbQuiz.answers[i].entryType === "fraction") {
                            let pattern = /[0-9.\/]/
                            if (cursorPos === 0 && input.val()[0] !== "-") {
                                pattern = /[-0-9.\/]/;
                            }
                            if (!pattern.test(e.key) && allowedKeys.indexOf(e.key) === -1)
                                e.preventDefault();
                        }
                        if (ftbQuiz.answers[i].entryType === "integer") {
                            let pattern = /[0-9]/
                            if (cursorPos === 0 && input.val()[0] !== "-") {
                                pattern = /[-0-9]/;
                            }
                            if (!pattern.test(e.key) && allowedKeys.indexOf(e.key) === -1)
                                e.preventDefault();
                        }
                    });
                    let blank = {
                        $el: input,
                        $err: err,
                        userAnswer: null,
                        type: "input",
                        answerObj: ftbQuiz.answers[i]
                    };
                    input.on('keyup', function () {
                        input.attr("value", input.val())
                        blank.userAnswer = input.val();
                        setSubmitStateText()
                    });
                    wrap.append(input, err);
                    let debounce;
                    blank.$el.on("input", () => {
                        blank.$err.fadeOut(100);
                        clearTimeout(debounce);
                        debounce = setTimeout(() => {
                            let inputVal = blank.$el.val().trim();
                            let validation = blank.answerObj.validate(inputVal)
                            if (validation.type === "warn") {
                                blank.$err.html(validation.text).fadeIn(100);
                            }
                        }, 750)
                    })
                    ftbQuiz.blanks.push(blank);
                    quizQuestionTitleEl.append(wrap);
                }
            }
        }
    });
}

function gcd(a, b) { if (b === 0) return a; return gcd(b, a % b) }

function getFractionValue(str) {
    return str.indexOf("/") !== -1 ? Number(str.split("/")[0]) / Number(str.split("/")[1]) : Number(str);
}

function getMeshDisplayName(uuid) {
    return "displayName" in Q3.data.assets.objects[uuid]
        ? Q3.data.assets.objects[uuid].displayName
        : Q3.assets.objects[uuid].name;
}

const awaitLib = (function () {
    let promise;
    return function () {
        return promise = (promise || new Promise(resolve => {
            if (window.MathJax) {
                resolve()
            } else {
                console.warn("Begun downloading MathJax");
                window.MathJax = {
                    tex: {
                        inlineMath: [['$', '$'], ['\\(', '\\)']]
                    },
                    svg: {
                        fontCache: 'global'
                    }
                };
                injectJS("/js/mathJax/tex-mml-chtml.js").then(function () {
                    console.info("MathJax downloaded.");
                    resolve();
                });
            }

        }));
    };
})();
